import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/contexts/RoleContext';
import { useCanViewOrgFinancials } from '@/hooks/useCanViewOrgFinancials';
import { StoragePhoto } from '@/components/StoragePhoto';
import { supabase, executeWithRetry } from '@/lib/supabase';
import {
  sessionDurationMinutes,
  updateWorkSessionTimes,
  WORK_SESSION_MAX_DISPLAY_HOURS,
  workSessionCloseReasonLabel,
  type WorkSessionCloseReason,
} from '@/lib/workSessions';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  Building,
  Loader2,
  Save,
  Plus,
  CreditCard,
  Edit,
  X,
  Lock,
  DollarSign,
  Snowflake,
  FileText,
  Camera,
  Image,
  CheckCircle2,
  RefreshCw,
  Trash2,
  History,
  RotateCcw,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { dateFnsLocaleFor } from '@/lib/appLocale';
import { validatePIN } from '@/lib/validation';
import ValidationChecklist from '@/components/ValidationChecklist';
import { SearchableSelect } from '@/components/admin/SearchableSelect';
import { CLEANING_STAFF_ROLES } from '@/lib/cleaningStaffRoles';
import {
  fetchStaffLocationAccessIds,
  grantStaffLocationAccess,
  revokeStaffLocationAccess,
} from '@/lib/locationAccess';
import { WorkerAssignedEquipmentCard } from '@/components/equipment/WorkerAssignedEquipmentCard';
import { firstJoined, isPlainObject, parseChecklistItems } from '@/lib/checklistJson';

type Profile = Database['public']['Tables']['profiles']['Row'];
type CleaningLocation = Database['public']['Tables']['cleaning_locations']['Row'];
type CleaningTask = Database['public']['Tables']['cleaning_tasks']['Row'];

interface ActivityTask extends CleaningTask {
  cleaning_locations?: {
    address: string;
  };
  property_sections?: {
    name: string;
  } | null;
  task_step_logs?: Array<{
    id: string;
    step_name: string;
    notes: string | null;
    photo_url: string | null;
    created_at: string;
  }>;
  has_notes?: boolean;
  has_photos?: boolean;
  /** Nazwa pomieszczenia z joinu cleaning_tasks -> property_sections (normalizowana przy pobieraniu). */
  sectionDisplayName?: string | null;
}

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface WorkerProfile extends Omit<Profile, 'emergency_contact' | 'account_type' | 'email' | 'phone' | 'address'> {
  base_hourly_rate: number;
  snow_hourly_rate: number;
  extra_hourly_rate: number;
  fixed_salary: number;
  emergency_contact: Record<string, unknown> | string;
  emergency_phone?: string | null;
  internal_notes: string | null;
  notes?: string | null;
  status?: string | null;
  employment_type?: string | null;
  phone_number?: string | null;
  account_type?: 'hub' | 'simplified';
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  login_email?: string | null;
  is_active?: boolean | null;
  is_archived?: boolean;
}

interface LocationWithAccess extends CleaningLocation {
  role?: string;
  last_visit?: string;
}

/** Single row in Rejestr czasu pracy (derived from cleaning_work_sessions). */
type WorkLogEntry = {
  id: string;
  date: string;
  location_id: string;
  location_address: string;
  started_at: string;
  completed_at: string;
  totalMinutes: number;
  displayMinutes: number;
  isSuspiciousLongSession: boolean;
  is_paid: boolean;
  status: string;
  close_reason: string | null;
  needs_review: boolean;
  check_out_distance_meters: number | null;
  is_open: boolean;
};

/** Parses numeric rate columns that Postgres may return as string. */
function toRateNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/** Extracts login (slug) from staff email, e.g. m.jozefiak3@staff.domio.com.pl → m.jozefiak3 */
function formatLoginDisplay(email: string | null | undefined, missingLabel: string): string {
  if (!email) return missingLabel;
  const m = email.match(/^(.+)@staff\.domio\.com\.pl$/i);
  return m ? m[1] : email;
}

interface RateHistoryEntry {
  id: string;
  staff_id: string;
  base_hourly_rate: number;
  snow_hourly_rate: number;
  extra_hourly_rate: number;
  fixed_salary: number;
  changed_at: string;
  changed_by: string | null;
  changed_by_display?: string;
}

export const WorkerDetailsPage = () => {
  const { id: workerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const dateLocale = dateFnsLocaleFor(i18n.language);
  const { currentOrgId, currentRole } = useRole();
  const canViewOrgFinancials = useCanViewOrgFinancials();

  const [worker, setWorker] = useState<WorkerProfile | null>(null);
  const [locations, setLocations] = useState<LocationWithAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadError, setIsLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit states - Financial rates
  const [isEditingFinancials, setIsEditingFinancials] = useState(false);
  const [editedFinancialData, setEditedFinancialData] = useState({
    base_hourly_rate: 0,
    snow_hourly_rate: 0,
    extra_hourly_rate: 0,
    fixed_salary: 0,
  });

  // Edit states - Data (independent editing for each card)
  const [isEditingEmergencyContact, setIsEditingEmergencyContact] = useState(false);
  const [isEditingInternalNotes, setIsEditingInternalNotes] = useState(false);
  const [isEditingProfileInfo, setIsEditingProfileInfo] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState({
    name: '',
    phone: '',
    relationship: '',
  });
  const [internalNotes, setInternalNotes] = useState('');
  const [profileInfo, setProfileInfo] = useState({
    phone: '',
    email: '',
    address: '',
  });
  const [workerEmail, setWorkerEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');

  // Modals
  const [isAddLocationDialogOpen, setIsAddLocationDialogOpen] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<CleaningLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Active tab state to prevent reset after save (support ?tab=payroll from FinancePage)
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    tabParam === 'payroll' ? 'payroll' : 'locations'
  );
  useEffect(() => {
    if (tabParam === 'payroll' && canViewOrgFinancials) setActiveTab('payroll');
  }, [tabParam, canViewOrgFinancials]);
  useEffect(() => {
    if (!canViewOrgFinancials && activeTab === 'payroll') {
      setActiveTab('locations');
    }
  }, [canViewOrgFinancials, activeTab]);
  useEffect(() => {
    const fromParam = searchParams.get('from');
    const toParam = searchParams.get('to');
    if (fromParam && toParam) {
      const from = new Date(fromParam);
      const to = new Date(toParam);
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        setPayrollDateRange({ from, to });
        setSelectedMonth(to.getMonth() + 1);
        setSelectedYear(to.getFullYear());
        return;
      }
    }
    const monthParam = searchParams.get('month');
    const yearParam = searchParams.get('year');
    if (monthParam && yearParam) {
      const m = parseInt(monthParam, 10);
      const y = parseInt(yearParam, 10);
      if (m >= 1 && m <= 12 && y >= 2020 && y <= 2030) {
        setSelectedMonth(m);
        setSelectedYear(y);
        setPayrollDateRange({
          from: new Date(y, m - 1, 1),
          to: new Date(y, m, 0),
        });
      }
    }
  }, [searchParams]);

  // Activity log states (Load More pagination)
  const ITEMS_PER_PAGE = 30;
  const [activityLog, setActivityLog] = useState<ActivityTask[]>([]);
  const [activityPage, setActivityPage] = useState(0);
  const [activityHasMore, setActivityHasMore] = useState(true);
  const [isLoadingActivityLog, setIsLoadingActivityLog] = useState(false);
  const [isLoadingMoreActivity, setIsLoadingMoreActivity] = useState(false);
  const [activityDateRange, setActivityDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [selectedActivityTask, setSelectedActivityTask] = useState<ActivityTask | null>(null);
  const [isActivityDetailsDialogOpen, setIsActivityDetailsDialogOpen] = useState(false);

  // Work logs: max display cap (avoid absurd values like 91h); entries above this are flagged
  const WORK_LOG_MAX_DISPLAY_HOURS = 16;

  // Work logs states (Load More pagination) – derived from cleaning_tasks (started_at, completed_at)
  const [workLogs, setWorkLogs] = useState<WorkLogEntry[]>([]);
  const [workLogsPage, setWorkLogsPage] = useState(0);
  const [workLogsHasMore, setWorkLogsHasMore] = useState(true);
  const [isLoadingWorkLogs, setIsLoadingWorkLogs] = useState(false);
  const [isLoadingMoreWorkLogs, setIsLoadingMoreWorkLogs] = useState(false);

  // Work log edit/delete (Admin only)
  const [workLogEdit, setWorkLogEdit] = useState<{ open: boolean; log: WorkLogEntry | null }>({ open: false, log: null });
  const [workLogDelete, setWorkLogDelete] = useState<{ open: boolean; log: WorkLogEntry | null }>({ open: false, log: null });
  const [editWorkLogForm, setEditWorkLogForm] = useState<{ started_at: string; completed_at: string; edit_reason: string }>({ started_at: '', completed_at: '', edit_reason: '' });
  const [isSavingWorkLog, setIsSavingWorkLog] = useState(false);
  const [isDeletingWorkLog, setIsDeletingWorkLog] = useState(false);

  // Payroll states
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [payrollDateRange, setPayrollDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [payrollData, setPayrollData] = useState<{
    totalAmount: number;
    paidAmount: number;
    unpaidAmount: number;
    totalEarnings: number;
    totalHours: number;
    baseRate: number;
    unpaidTaskIds: string[];
    details: Array<{
      type: 'base' | 'bonus' | 'adjustment';
      description: string;
      hours: number | null;
      rate: number | null;
      amount: number;
      date: string;
      task_id?: string;
      is_paid?: boolean;
    }>;
  } | null>(null);
  const [isLoadingPayroll, setIsLoadingPayroll] = useState(false);
  const [isAddAdjustmentDialogOpen, setIsAddAdjustmentDialogOpen] = useState(false);
  const [newAdjustment, setNewAdjustment] = useState({
    amount: '',
    description: '',
  });
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());

  // Rate history (staff_rate_history)
  const [rateHistory, setRateHistory] = useState<RateHistoryEntry[]>([]);
  const [isLoadingRateHistory, setIsLoadingRateHistory] = useState(false);

  // Refs to avoid refetch/recalc when only switching back to the same tab (forceMount keeps data)
  const lastActivityFetchKeyRef = useRef<string | null>(null);
  const lastPayrollFetchKeyRef = useRef<string | null>(null);

  const canEdit = currentRole === 'owner' || currentRole === 'coordinator';

  // Email validation function
  const validateEmail = (email: string): boolean => {
    if (!email || email.trim() === '') {
      return true; // Empty email is valid (not required)
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    // Fix loading: Set loading to false if workerId or currentOrgId are missing
    if (!workerId || !currentOrgId) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    fetchWorkerData();
  }, [workerId, currentOrgId]);

  useEffect(() => {
    if (activeTab !== 'activity-log' || !workerId || !currentOrgId) return;
    const from = activityDateRange.from?.getTime();
    const to = activityDateRange.to?.getTime();
    const key = `${workerId}|${from ?? ''}|${to ?? ''}`;
    // Skip refetch when switching back to tab: we already have data for this key (forceMount keeps DOM)
    if (activityLog.length > 0 && lastActivityFetchKeyRef.current === key) return;
    lastActivityFetchKeyRef.current = key;
    setActivityPage(0);
    setActivityHasMore(true);
    setWorkLogsPage(0);
    setWorkLogsHasMore(true);
    fetchActivityLog(workerId, 0, false);
    fetchWorkLogs(workerId, 0, false);
  }, [activeTab, activityDateRange, workerId, currentOrgId, activityLog.length]);

  useEffect(() => {
    if (activeTab !== 'payroll' || !canViewOrgFinancials || !workerId || !currentOrgId || !worker) return;
    const prFrom = payrollDateRange.from?.getTime();
    const prTo = payrollDateRange.to?.getTime();
    const key = `${workerId}|${selectedMonth}|${selectedYear}|${prFrom ?? ''}|${prTo ?? ''}|${i18n.language}`;
    // Skip recalc when switching back to tab: we already have data for this key (forceMount keeps DOM)
    if (payrollData !== null && lastPayrollFetchKeyRef.current === key) return;
    lastPayrollFetchKeyRef.current = key;
    calculateMonthlyPayroll();
  }, [activeTab, selectedMonth, selectedYear, payrollDateRange, workerId, currentOrgId, worker, payrollData, canViewOrgFinancials, i18n.language]);

  useEffect(() => {
    if (activeTab === 'payroll' && canViewOrgFinancials && workerId) {
      fetchRateHistory();
    }
  }, [activeTab, workerId, canViewOrgFinancials]);

  const fetchWorkerData = async () => {
    if (!workerId || !currentOrgId) {
      toast.error(t('admin.worker.missingContext'));
      return;
    }

    setIsLoading(true);
    setIsLoadError(false);
    try {
      await executeWithRetry(async () => {
        // Fetch worker profile (profiles) and staff row (cleaning_staff) in parallel
        // cleaning_staff.id = workerId (profile id), not staff_id
        const [profileResult, staffResult, membershipResult] = await Promise.all([
          supabase
            .from('profiles')
            .select('*')
            .eq('id', workerId)
            .single(),
          supabase
            .from('cleaning_staff')
            .select('id, full_name, emergency_contact, emergency_phone, notes, status, employment_type, phone, org_id, is_active, created_at, internal_id, pin, contact_email')
            .eq('id', workerId)
            .maybeSingle(),
          supabase
            .from('memberships')
            .select('is_active, role')
            .eq('user_id', workerId)
            .eq('org_id', currentOrgId)
            .in('role', [...CLEANING_STAFF_ROLES]),
        ]);

      const { data: profile, error: profileError } = profileResult;
      const { data: staff } = staffResult;
      const cleaningMemberships = membershipResult.data ?? [];
      const membershipInactive =
        cleaningMemberships.length > 0 &&
        cleaningMemberships.every((m) => m.is_active === false);
      const staffInactive = staff?.is_active === false || staff?.status === 'archived';
      const isArchived = membershipInactive || staffInactive;

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        toast.error(t('admin.worker.fetchWorkerError'));
        return;
      }

      if (!profile) {
        toast.error(t('admin.worker.notFound'));
        navigate('/');
        return;
      }

      // Notes come from cleaning_staff.notes; fallback to profiles.internal_notes for legacy
      const staffNotes = staff?.notes ?? null;
      const profileNotes = profile.internal_notes ?? null;
      const preferences = isPlainObject(profile.preferences) ? profile.preferences : {};

      const loginEmail =
        profile.email ||
        (staff?.internal_id ? `${staff.internal_id}@staff.domio.com.pl` : '');
      const contactEmailRaw =
        staff?.contact_email ||
        profile.contact_email ||
        (typeof preferences.email === 'string' ? preferences.email : '') ||
        '';
      const contactEmail = /@staff\.domio\.com\.pl$/i.test(contactEmailRaw)
        ? ''
        : contactEmailRaw;
      const accountTypeRaw = (profile.account_type || '').toLowerCase();
      const accountType: 'hub' | 'simplified' =
        accountTypeRaw === 'hub' || accountTypeRaw === 'standard'
          ? 'hub'
          : 'simplified';

      const workerData: WorkerProfile = {
        ...profile,
        base_hourly_rate: toRateNumber(profile.base_hourly_rate),
        snow_hourly_rate: toRateNumber(profile.snow_hourly_rate),
        extra_hourly_rate: toRateNumber(profile.extra_hourly_rate),
        fixed_salary: toRateNumber(profile.fixed_salary),
        is_active: staff?.is_active ?? !isArchived,
        is_archived: isArchived,
        email: contactEmail,
        login_email: loginEmail || null,

        // NAPRAWA AWARYJNYCH: Pobieramy jawnie ze staff (DB kolumna text – parsujemy JSON)
        emergency_contact: (() => {
          const raw = staff?.emergency_contact ?? profile.emergency_contact;
          if (raw == null || raw === '') return '';
          if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
          if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw);
              return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : '';
            } catch {
              return raw;
            }
          }
          return '';
        })(),
        emergency_phone: staff?.emergency_phone || '',

        notes: staff?.notes || '',
        internal_notes: staffNotes ?? profileNotes ?? null,

        phone: profile.phone ?? staff?.phone ?? (typeof preferences.phone === 'string' ? preferences.phone : null),
        address: profile.address ?? (typeof preferences.address === 'string' ? preferences.address : null),
        account_type: accountType,
      };

      setWorker(workerData);
      setEmergencyContact(
        (workerData.emergency_contact && typeof workerData.emergency_contact === 'object' && !Array.isArray(workerData.emergency_contact))
          ? {
              name: String(workerData.emergency_contact.name ?? ''),
              phone: String(workerData.emergency_contact.phone ?? ''),
              relationship: String(workerData.emergency_contact.relationship ?? ''),
            }
          : { name: '', phone: '', relationship: '' }
      );
      setInternalNotes(workerData.internal_notes ?? '');
      setProfileInfo({
        phone: workerData.phone ?? '',
        email: workerData.email ?? '',
        address: workerData.address ?? '',
      });
      setWorkerEmail(workerData.login_email || '');
      setEditedFinancialData({
        base_hourly_rate: workerData.base_hourly_rate || 0,
        snow_hourly_rate: workerData.snow_hourly_rate || 0,
        extra_hourly_rate: workerData.extra_hourly_rate || 0,
        fixed_salary: workerData.fixed_salary || 0,
      });

      // Fetch locations from location_access
      await fetchLocations(workerId);

      // Fetch activity log if on activity tab
      if (activeTab === 'activity-log') {
        await fetchActivityLog(workerId, 0, false);
      }
      }, { isReadOperation: true });
    } catch (error: unknown) {
      console.error('Error fetching worker data:', error);
      toast.error(error instanceof Error ? error.message : t('admin.worker.fetchDataError'));
      setIsLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async (userId: string) => {
    if (!currentOrgId) return;

    try {
      const locationIds = await fetchStaffLocationAccessIds(userId);

      if (locationIds.length === 0) {
        setLocations([]);
        return;
      }

      // Fetch locations
      const { data: locationsData, error: locationsError } = await supabase
        .from('cleaning_locations')
        .select('*')
        .in('id', locationIds)
        .eq('org_id', currentOrgId)
        .eq('is_cleaning_active', true)
        .eq('status', 'active');

      if (locationsError) {
        console.error('Error fetching locations:', locationsError);
        return;
      }

      // Fetch last visit dates from cleaning_tasks
      const { data: tasksData } = await supabase
        .from('cleaning_tasks')
        .select('location_id, completed_at')
        .eq('assigned_staff_id', userId)
        .eq('status', 'done')
        .in('location_id', locationIds)
        .order('completed_at', { ascending: false });

      // Create map of last visit dates
      const lastVisitMap = new Map<string, string>();
      if (tasksData) {
        tasksData.forEach(task => {
          if (task.location_id && task.completed_at) {
            const existing = lastVisitMap.get(task.location_id);
            if (!existing || task.completed_at > existing) {
              lastVisitMap.set(task.location_id, task.completed_at);
            }
          }
        });
      }

      // Determine role for each location
      const locationsWithRole: LocationWithAccess[] = (locationsData || []).map(loc => {
        const lastVisit = lastVisitMap.get(loc.id);
        return {
          ...loc,
          
          last_visit: lastVisit || undefined,
        };
      });

      setLocations(locationsWithRole);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchAvailableLocations = async () => {
    if (!currentOrgId || !workerId) return;

    try {
      const assignedIds = await fetchStaffLocationAccessIds(workerId);
      const existingLocationIds = new Set(assignedIds);

      // Fetch all locations in org that worker doesn't have access to
      const { data: locationsData, error } = await supabase
        .from('cleaning_locations')
        .select('*')
        .eq('org_id', currentOrgId)
        .eq('is_cleaning_active', true)
        .eq('status', 'active');

      if (error) {
        console.error('Error fetching available locations:', error);
        return;
      }

      const available = (locationsData || []).filter(
        loc => !existingLocationIds.has(loc.id)
      );

      setAvailableLocations(available);
    } catch (error) {
      console.error('Error fetching available locations:', error);
    }
  };

  const fetchWorkerEmail = async (userId: string) => {
    try {
      // For simplified accounts, email is stored in auth.users
      // We'll try to get it via RPC function or use profiles.email as fallback
      // Note: Direct admin API access may not be available in browser context
      // For now, we'll use profiles.email if available, otherwise try to get from auth
      
      // Try to get email from profiles first
      if (worker?.email) {
        setWorkerEmail(worker.email);
        return;
      }

      // If not in profiles, try to get from auth.users via RPC (if available)
      // For now, we'll leave it empty and let user fill it in edit mode
      // The email will be stored in profiles.email after save
      setWorkerEmail('');
    } catch (error) {
      console.error('Error fetching worker email:', error);
      // Fallback to profiles.email
      if (worker?.email) {
        setWorkerEmail(worker.email);
      }
    }
  };

  const fetchActivityLog = async (userId: string, page: number, append: boolean) => {
    if (!currentOrgId) return;

    const from = page * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    try {
      if (page === 0) {
        setIsLoadingActivityLog(true);
      } else {
        setIsLoadingMoreActivity(true);
      }

      // cleaning_tasks -> section_id (FK) -> property_sections(name); alias property_sections dla zagnieżdżonego joinu
      let query = supabase
        .from('cleaning_tasks')
        .select(`
          *,
          cleaning_locations(address),
          property_sections:section_id(name)
        `)
        .eq('assigned_staff_id', userId)
        .eq('org_id', currentOrgId)
        .eq('status', 'done')
        .order('completed_at', { ascending: false });

      if (activityDateRange.from) {
        query = query.gte('completed_at', activityDateRange.from.toISOString());
      }
      if (activityDateRange.to) {
        const toDate = new Date(activityDateRange.to);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('completed_at', toDate.toISOString());
      }

      const { data: tasks, error: tasksError } = await query.range(from, to);

      if (tasksError) {
        console.error('Error fetching activity log:', tasksError);
        toast.error(t('admin.worker.fetchActivityError'));
        return;
      }

      const taskIds = (tasks || []).map(t => t.id);
      let stepLogsMap: Record<string, ActivityTask['task_step_logs']> = {};

      if (taskIds.length > 0) {
        const { data: stepLogs, error: stepLogsError } = await supabase
          .from('task_step_logs')
          .select('id, task_id, step_name, photo_url, created_at')
          .in('task_id', taskIds)
          .order('created_at', { ascending: true });

        if (!stepLogsError && stepLogs && Array.isArray(stepLogs)) {
          stepLogs.forEach((log) => {
            const taskId = log.task_id;
            if (!taskId) return;
            if (!stepLogsMap[taskId]) {
              stepLogsMap[taskId] = [];
            }
            stepLogsMap[taskId].push({
              id: log.id,
              step_name: log.step_name ?? '',
              notes: null,
              photo_url: log.photo_url,
              created_at: log.created_at ?? '',
            });
          });
        }
      }

      // Helper: odczyt nazwy sekcji z joinu (Supabase może zwrócić property_sections lub section_id jako obiekt)
      const getSectionName = (t: {
        property_sections?: { name: string } | { name: string }[] | null;
      }): string | null => {
        const ps = firstJoined(t.property_sections);
        const name = ps?.name;
        if (typeof name === 'string' && name.trim()) return name.trim();
        return null;
      };

      const enrichedTasks: ActivityTask[] = (tasks || []).map(task => {
        const stepLogs = stepLogsMap[task.id] || [];
        const hasNotes = !!task.staff_notes || stepLogs.some(log => log.notes);
        const hasPhotos = stepLogs.some(log => log.photo_url);
        const sectionData = firstJoined(task.property_sections);
        const sectionDisplayName = getSectionName(task);
        return {
          ...task,
          property_sections: sectionData,
          task_step_logs: stepLogs,
          has_notes: hasNotes,
          has_photos: hasPhotos,
          sectionDisplayName,
        };
      });

      setActivityHasMore((tasks?.length ?? 0) >= ITEMS_PER_PAGE);
      if (append) {
        setActivityLog(prev => [...prev, ...enrichedTasks]);
      } else {
        setActivityLog(enrichedTasks);
      }
    } catch (error) {
      console.error('Error fetching activity log:', error);
      toast.error(t('admin.worker.fetchActivityUnexpected'));
    } finally {
      setIsLoadingActivityLog(false);
      setIsLoadingMoreActivity(false);
    }
  };

  const fetchWorkLogs = async (userId: string, page: number, append: boolean) => {
    if (!currentOrgId) return;

    const from = page * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    try {
      if (page === 0) {
        setIsLoadingWorkLogs(true);
      } else {
        setIsLoadingMoreWorkLogs(true);
      }

      let query = supabase
        .from('cleaning_work_sessions')
        .select('id, location_id, started_at, ended_at, status, close_reason, needs_review, is_paid, check_out_distance_meters, cleaning_locations(address)')
        .eq('user_id', userId)
        .eq('org_id', currentOrgId)
        .order('started_at', { ascending: false })
        .range(from, to);

      const { data: sessions, error } = await query;

      if (error) {
        console.error('Error fetching work logs:', error);
        toast.error(t('admin.worker.fetchWorkLogError'));
        return;
      }

      const maxDisplayMinutes = WORK_SESSION_MAX_DISPLAY_HOURS * 60;
      const rows = (sessions || []).map((session) => {
        const endedAt = session.ended_at ?? new Date().toISOString();
        const totalMinutes = sessionDurationMinutes(session.started_at, endedAt);
        const isSuspiciousLongSession = totalMinutes > maxDisplayMinutes || session.needs_review || session.status === 'open';
        const displayMinutes = totalMinutes > maxDisplayMinutes ? maxDisplayMinutes : totalMinutes;
        const loc = firstJoined(session.cleaning_locations);
        const address = loc?.address;
        return {
          id: session.id,
          date: session.ended_at ?? session.started_at,
          location_id: session.location_id || '',
          location_address: address || '',
          started_at: session.started_at,
          completed_at: endedAt,
          totalMinutes,
          displayMinutes,
          isSuspiciousLongSession,
          is_paid: !!session.is_paid,
          status: session.status,
          close_reason: session.close_reason,
          needs_review: session.needs_review,
          check_out_distance_meters: session.check_out_distance_meters,
          is_open: session.status === 'open',
        };
      });

      setWorkLogsHasMore((sessions?.length ?? 0) >= ITEMS_PER_PAGE);
      if (append) {
        setWorkLogs(prev => [...prev, ...rows]);
      } else {
        setWorkLogs(rows);
      }
    } catch (err) {
      console.error('Error fetching work logs:', err);
      toast.error(t('admin.worker.fetchWorkLogUnexpected'));
    } finally {
      setIsLoadingWorkLogs(false);
      setIsLoadingMoreWorkLogs(false);
    }
  };

  const calculateMonthlyPayroll = async () => {
    if (!workerId || !currentOrgId || !worker) return;

    try {
      setIsLoadingPayroll(true);

      // Priority: use payrollDateRange if set, otherwise use selectedMonth/selectedYear
      let monthStart: Date;
      let monthEnd: Date;

      if (payrollDateRange.from && payrollDateRange.to) {
        // Use date range
        monthStart = new Date(payrollDateRange.from);
        monthStart.setHours(0, 0, 0, 0);
        monthEnd = new Date(payrollDateRange.to);
        monthEnd.setHours(23, 59, 59, 999);
      } else {
        // Fallback to month/year selector
        monthStart = new Date(selectedYear, selectedMonth - 1, 1);
        monthEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59);
      }

      // Fetch completed tasks for selected month (include is_paid for unpaid filter)
      const { data: tasksData, error: tasksError } = await supabase
        .from('cleaning_tasks')
        .select('id, started_at, completed_at, total_cost, is_paid, applied_hourly_rate, task_type, checklist, property_sections(name), cleaning_locations(address)')
        .eq('assigned_staff_id', workerId)
        .eq('status', 'done')
        .eq('org_id', currentOrgId)
        .gte('completed_at', monthStart.toISOString())
        .lte('completed_at', monthEnd.toISOString());

      if (tasksError) {
        console.error('Error fetching tasks:', tasksError);
        toast.error(t('admin.worker.fetchTasksError'));
        return;
      }

      const baseRate = worker.base_hourly_rate || 0;
      const snowRate = worker.snow_hourly_rate || 0;
      const fixedSalary = worker.fixed_salary || 0;

      let totalHours = 0;
      let totalCleaningHours = 0;
      let totalSnowHours = 0;
      let paidTaskAmount = 0;
      let unpaidTaskAmount = 0;
      const unpaidTaskIds: string[] = [];
      const details: Array<{
        type: 'base' | 'bonus' | 'adjustment';
        description: string;
        hours: number | null;
        rate: number | null;
        amount: number;
        date: string;
        task_id?: string;
        is_paid?: boolean;
      }> = [];

      // Process tasks: PRIORITY: use total_cost if set (snapshot), else fallback to dynamic calculation (hours * rate)
      if (tasksData) {
        tasksData.forEach(task => {
          const taskTotalCost = task.total_cost != null ? parseFloat(String(task.total_cost)) : null;
          const useSnapshot = taskTotalCost != null && taskTotalCost > 0;
          if (task.task_type === 'extra_paid' && !useSnapshot) {
            return;
          }
          let hours = 0;
          if (task.started_at && task.completed_at) {
            const start = new Date(task.started_at);
            const end = new Date(task.completed_at);
            const rawHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
            hours = Math.min(rawHours, WORK_LOG_MAX_DISPLAY_HOURS);
          }
          const wasCapped = hours >= WORK_LOG_MAX_DISPLAY_HOURS;

          // Detect snow removal - check task name in checklist or section name
          let isSnowTask = false;

          const sectionName = firstJoined(task.property_sections)?.name || '';

          const checklist = parseChecklistItems(task.checklist);

          // Check section name
          if (sectionName && (
            sectionName.toLowerCase().includes('odśnieżanie') ||
            sectionName.toLowerCase().includes('śnieg') ||
            sectionName.toLowerCase().includes('snow')
          )) {
            isSnowTask = true;
          }

          // Check checklist items
          if (!isSnowTask && checklist.length > 0) {
            for (const item of checklist) {
              const itemName = item.name || item.task_name || '';
              if (itemName && (
                itemName.toLowerCase().includes('odśnieżanie') ||
                itemName.toLowerCase().includes('śnieg') ||
                itemName.toLowerCase().includes('snow')
              )) {
                isSnowTask = true;
                break;
              }
            }
          }

          totalHours += hours;

          // Get location address (Supabase may return array or object)
          let locationAddress = t('admin.worker.noAddress');
          const joinedLocation = firstJoined(task.cleaning_locations);
          if (joinedLocation?.address) {
            locationAddress = joinedLocation.address;
          }

          // PRIORITY: use snapshot total_cost if available; else fallback to hours * rate (for old tasks with null total_cost)
          const taskAppliedRate = task.applied_hourly_rate != null ? parseFloat(String(task.applied_hourly_rate)) : null;

          const amount = useSnapshot
            ? taskTotalCost!
            : isSnowTask
              ? hours * snowRate
              : hours * baseRate;
          const displayRate = useSnapshot && taskAppliedRate != null ? taskAppliedRate : (isSnowTask ? snowRate : baseRate);

          const isPaid = task.is_paid === true;
          if (isPaid) {
            paidTaskAmount += amount;
          } else {
            unpaidTaskAmount += amount;
            unpaidTaskIds.push(task.id);
          }

          if (isSnowTask) {
            totalSnowHours += hours;
            details.push({
              type: 'base',
              description: t('admin.worker.payrollLine', { kind: t('admin.worker.payrollSnow'), address: locationAddress, limit: wasCapped ? t('admin.worker.payrollHourLimit') : '', rate: taskAppliedRate != null ? t('admin.worker.payrollRateSuffix', { rate: taskAppliedRate.toFixed(2) }) : '' }),
              hours: parseFloat(hours.toFixed(2)),
              rate: displayRate,
              amount: parseFloat(amount.toFixed(2)),
              date: task.completed_at || task.started_at || '',
              task_id: task.id,
              is_paid: isPaid,
            });
          } else if (task.task_type === 'extra_paid') {
            details.push({
              type: 'base',
              description: t('admin.worker.payrollLine', { kind: t('admin.worker.payrollExtra'), address: locationAddress, limit: wasCapped ? t('admin.worker.payrollHourLimit') : '', rate: taskAppliedRate != null ? t('admin.worker.payrollRateSuffix', { rate: taskAppliedRate.toFixed(2) }) : '' }),
              hours: parseFloat(hours.toFixed(2)),
              rate: displayRate,
              amount: parseFloat(amount.toFixed(2)),
              date: task.completed_at || task.started_at || '',
              task_id: task.id,
              is_paid: isPaid,
            });
          } else {
            totalCleaningHours += hours;
            details.push({
              type: 'base',
              description: t('admin.worker.payrollLine', { kind: t('admin.worker.payrollCleaning'), address: locationAddress, limit: wasCapped ? t('admin.worker.payrollHourLimit') : '', rate: taskAppliedRate != null ? t('admin.worker.payrollRateSuffix', { rate: taskAppliedRate.toFixed(2) }) : '' }),
              hours: parseFloat(hours.toFixed(2)),
              rate: displayRate,
              amount: parseFloat(amount.toFixed(2)),
              date: task.completed_at || task.started_at || '',
              task_id: task.id,
              is_paid: isPaid,
            });
          }
        });
      }

      // Fetch staff_payouts for this worker and months overlapping with period (for fixed salary status)
      const monthsInRange: { month: number; year: number }[] = [];
      let d = new Date(monthStart);
      d.setDate(1);
      while (d <= monthEnd) {
        monthsInRange.push({ month: d.getMonth() + 1, year: d.getFullYear() });
        d.setMonth(d.getMonth() + 1);
      }
      const { data: payoutsData } = await supabase
        .from('staff_payouts')
        .select('total_amount, month, year')
        .eq('staff_id', workerId)
        .eq('org_id', currentOrgId);

      const totalPayoutsForPeriod = (payoutsData || []).reduce((sum, p) => {
        const overlaps = monthsInRange.some((m) => m.month === p.month && m.year === p.year);
        return overlaps ? sum + parseFloat(String(p.total_amount)) : sum;
      }, 0);
      const paidTasksAmount = paidTaskAmount;
      let paidSalaryPart = Math.max(0, totalPayoutsForPeriod - paidTasksAmount);

      // Add virtual row(s) for fixed salary - one per month in range
      if (fixedSalary > 0) {
        for (const { month: m, year: y } of monthsInRange) {
          const virtualId = `fixed-salary-${m}-${y}`;
          const fixedSalaryPaid = paidSalaryPart >= fixedSalary;
          const fixedRowAmount = fixedSalaryPaid ? fixedSalary : Math.max(0, fixedSalary - paidSalaryPart);
          if (fixedSalaryPaid) paidSalaryPart -= fixedSalary;
          else paidSalaryPart = 0;

          details.unshift({
            type: 'base',
            description: t('admin.worker.payrollFixed'),
            hours: null,
            rate: null,
            amount: parseFloat(fixedRowAmount.toFixed(2)),
            date: new Date(y, m - 1, 1).toISOString(),
            task_id: virtualId,
            is_paid: fixedSalaryPaid,
          });
        }
      }

      // Fetch financial adjustments for selected month
      const monthStartStr = monthStart.toISOString();
      const monthEndStr = monthEnd.toISOString();

      const { data: adjustmentsData, error: adjustmentsError } = await supabase
        .from('staff_financial_adjustments')
        .select('id, amount, reason, type, created_at')
        .eq('user_id', workerId)
        .gte('created_at', monthStartStr)
        .lte('created_at', monthEndStr)
        .order('created_at', { ascending: false });

      if (adjustmentsError) {
        console.error('Error fetching financial adjustments:', adjustmentsError);
        // Nie przerywamy - kontynuujemy bez korekt finansowych
      } else if (adjustmentsData && Array.isArray(adjustmentsData) && adjustmentsData.length > 0) {
        adjustmentsData.forEach(adj => {
          const amount = parseFloat(adj.amount.toString());
          details.push({
            type: adj.type === 'bonus' ? 'bonus' : (adj.type === 'potrącenie' ? 'adjustment' : (amount >= 0 ? 'bonus' : 'adjustment')),
            description: adj.reason || (amount >= 0 ? t('admin.worker.adjustmentBonusFallback') : t('admin.worker.adjustmentCorrectionFallback')),
            hours: null,
            rate: null,
            amount: parseFloat(amount.toFixed(2)),
            date: adj.created_at || '',
          });
        });
      }

      // Calculate total earnings (sum from details - respects snapshot total_cost when used)
      const baseEarningsFromDetails = details
        .filter(d => d.type === 'base')
        .reduce((sum, d) => sum + d.amount, 0);
      const adjustmentsTotal = details
        .filter(d => d.type === 'bonus' || d.type === 'adjustment')
        .reduce((sum, d) => sum + d.amount, 0);
      const totalEarnings = baseEarningsFromDetails + adjustmentsTotal;

      // Split by payment status: paidAmount = tasks with is_paid, unpaidAmount = tasks with !is_paid
      const unpaidAmount = parseFloat(unpaidTaskAmount.toFixed(2));
      const paidAmount = parseFloat(paidTaskAmount.toFixed(2));
      const totalAmount = parseFloat(totalEarnings.toFixed(2));

      // Sort details by date (newest first)
      details.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setPayrollData({
        totalAmount,
        paidAmount,
        unpaidAmount,
        totalEarnings: totalAmount,
        totalHours: parseFloat(totalHours.toFixed(2)),
        baseRate: baseRate,
        unpaidTaskIds,
        details,
      });
      setSelectedTaskIds(new Set());
    } catch (error) {
      console.error('Error calculating monthly payroll:', error);
      toast.error(t('admin.worker.payrollCalcError'));
    } finally {
      setIsLoadingPayroll(false);
    }
  };

  const fetchRateHistory = async () => {
    if (!workerId) return;
    setIsLoadingRateHistory(true);
    try {
      const { data, error } = await supabase
        .from('staff_rate_history')
        .select('id, staff_id, base_hourly_rate, snow_hourly_rate, extra_hourly_rate, fixed_salary, changed_at, changed_by')
        .eq('staff_id', workerId)
        .order('changed_at', { ascending: false });

      if (error) {
        console.error('Error fetching rate history:', error);
        setRateHistory([]);
        return;
      }

      const entries = (data || []) as RateHistoryEntry[];
      if (entries.length === 0) {
        setRateHistory([]);
        return;
      }

      // Optionally resolve changed_by to full_name
      const changedByIds = [...new Set(entries.map((e) => e.changed_by).filter(Boolean))] as string[];
      let changedByMap: Record<string, string> = {};
      if (changedByIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', changedByIds);
        if (profiles) {
          profiles.forEach((p) => {
            changedByMap[p.id] = p.full_name || '—';
          });
        }
      }

      setRateHistory(
        entries.map((e) => ({
          ...e,
          changed_by_display: e.changed_by ? (changedByMap[e.changed_by] || undefined) : undefined,
        }))
      );
    } catch (err) {
      console.error('Error fetching rate history:', err);
      setRateHistory([]);
    } finally {
      setIsLoadingRateHistory(false);
    }
  };

  const handleSaveFinancials = async () => {
    if (!workerId || !currentOrgId) return;

    try {
      setIsSaving(true);

      const { data: updated, error: updateError } = await supabase
        .from('profiles')
        .update({
          base_hourly_rate: editedFinancialData.base_hourly_rate,
          snow_hourly_rate: editedFinancialData.snow_hourly_rate,
          extra_hourly_rate: editedFinancialData.extra_hourly_rate,
          fixed_salary: editedFinancialData.fixed_salary,
        })
        .eq('id', workerId)
        .select('id, base_hourly_rate, snow_hourly_rate, extra_hourly_rate, fixed_salary')
        .maybeSingle();

      if (updateError) {
        console.error('Error updating financials:', updateError);
        toast.error(t('admin.worker.saveFinancialsError'));
        return;
      }

      if (!updated) {
        toast.error(t('admin.worker.ratesPermissionError'));
        return;
      }

      toast.success(t('admin.worker.financialsSaved'));
      setIsEditingFinancials(false);
      await fetchWorkerData();
      await fetchRateHistory();
    } catch (error) {
      console.error('Error saving financials:', error);
      toast.error(t('admin.worker.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveWorker = async () => {
    if (!workerId || !currentOrgId || !canEdit) return;

    const { data: authData } = await supabase.auth.getUser();
    if (authData.user?.id === workerId) {
      toast.error(t('admin.worker.cannotArchiveSelf'));
      return;
    }

    if (!confirm(t('admin.worker.archiveConfirm'))) {
      return;
    }

    try {
      setIsSaving(true);

      const { data: membershipsUpdated, error: membershipError } = await supabase
        .from('memberships')
        .update({ is_active: false })
        .eq('user_id', workerId)
        .eq('org_id', currentOrgId)
        .in('role', [...CLEANING_STAFF_ROLES])
        .select('id');

      if (membershipError) {
        console.error('Error archiving worker memberships:', membershipError);
        toast.error(t('admin.worker.archiveError'));
        return;
      }

      if (!membershipsUpdated || membershipsUpdated.length === 0) {
        toast.error(t('admin.worker.archiveMembershipMissing'));
        return;
      }

      const { error: staffError } = await supabase
        .from('cleaning_staff')
        .update({ is_active: false, status: 'archived' })
        .eq('id', workerId)
        .eq('org_id', currentOrgId);

      if (staffError) {
        console.error('Error archiving cleaning_staff row:', staffError);
      }

      toast.success(t('admin.worker.archivedSuccess'));
      await fetchWorkerData();
    } catch (error) {
      console.error('Unexpected error archiving worker:', error);
      toast.error(t('admin.worker.unexpectedError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestoreWorker = async () => {
    if (!workerId || !currentOrgId || !canEdit) return;

    try {
      setIsSaving(true);

      const { data: membershipsUpdated, error: membershipError } = await supabase
        .from('memberships')
        .update({ is_active: true })
        .eq('user_id', workerId)
        .eq('org_id', currentOrgId)
        .in('role', [...CLEANING_STAFF_ROLES])
        .select('id');

      if (membershipError) {
        console.error('Error restoring worker memberships:', membershipError);
        toast.error(t('admin.worker.restoreError'));
        return;
      }

      if (!membershipsUpdated || membershipsUpdated.length === 0) {
        toast.error(t('admin.worker.restoreMembershipMissing'));
        return;
      }

      const { error: staffError } = await supabase
        .from('cleaning_staff')
        .update({ is_active: true, status: 'active' })
        .eq('id', workerId)
        .eq('org_id', currentOrgId);

      if (staffError) {
        console.error('Error restoring cleaning_staff row:', staffError);
      }

      toast.success(t('admin.worker.restoredSuccess'));
      await fetchWorkerData();
    } catch (error) {
      console.error('Unexpected error restoring worker:', error);
      toast.error(t('admin.worker.unexpectedError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditFinancials = () => {
    if (worker) {
      setEditedFinancialData({
        base_hourly_rate: worker.base_hourly_rate || 0,
        snow_hourly_rate: worker.snow_hourly_rate || 0,
        extra_hourly_rate: worker.extra_hourly_rate || 0,
        fixed_salary: worker.fixed_salary || 0,
      });
    }
    setIsEditingFinancials(false);
  };

  const handleSaveEmergencyContact = async () => {
    if (!workerId || !currentOrgId) return;

    try {
      setIsSaving(true);

      // Kontakt awaryjny w cleaning_staff (kolumna emergency_contact jest text – zapis jako JSON)
      const { error: staffError } = await supabase
        .from('cleaning_staff')
        .update({
          emergency_contact: typeof emergencyContact === 'object' && emergencyContact !== null
            ? JSON.stringify(emergencyContact)
            : String(emergencyContact ?? ''),
          emergency_phone: emergencyContact?.phone ?? null,
        })
        .eq('id', workerId);

      if (staffError) {
        console.error('Error updating emergency contact (cleaning_staff):', staffError);
        toast.error(t('admin.worker.saveEmergencyError'));
        return;
      }

      toast.success(t('admin.worker.emergencySaved'));
      setIsEditingEmergencyContact(false);
      await fetchWorkerData();
    } catch (error) {
      console.error('Error saving emergency contact:', error);
      toast.error(t('admin.worker.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditEmergencyContact = () => {
    if (worker) {
      const contact = isPlainObject(worker.emergency_contact)
        ? {
            name: String(worker.emergency_contact.name ?? ''),
            phone: String(worker.emergency_contact.phone ?? ''),
            relationship: String(worker.emergency_contact.relationship ?? ''),
          }
        : { name: '', phone: '', relationship: '' };
      setEmergencyContact(contact);
    }
    setIsEditingEmergencyContact(false);
  };

  const handleSaveInternalNotes = async () => {
    if (!workerId || !currentOrgId) return;

    try {
      setIsSaving(true);

      // Notatki w cleaning_staff (spójne z kontaktem awaryjnym)
      const { error: staffError } = await supabase
        .from('cleaning_staff')
        .update({ notes: internalNotes })
        .eq('id', workerId);

      if (staffError) {
        // Fallback: zapis do profiles.internal_notes, jeśli cleaning_staff nie ma kolumny notes
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ internal_notes: internalNotes })
          .eq('id', workerId);

        if (profileError) {
          console.error('Error updating internal notes:', staffError, profileError);
          toast.error(t('admin.worker.saveNotesError'));
          return;
        }
      }

      toast.success(t('admin.worker.notesSaved'));
      setIsEditingInternalNotes(false);
      await fetchWorkerData();
    } catch (error) {
      console.error('Error saving internal notes:', error);
      toast.error(t('admin.worker.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditInternalNotes = () => {
    if (worker) {
      setInternalNotes(worker.internal_notes ?? '');
    }
    setIsEditingInternalNotes(false);
  };

  const handleSaveProfileInfo = async () => {
    if (!workerId || !currentOrgId) return;

    // Validate email before saving
    if (!validateEmail(profileInfo.email)) {
      toast.error(t('admin.worker.invalidEmailToast'));
      setEmailError('invalid');
      return;
    }

    try {
      setIsSaving(true);
      setEmailError(''); // Clear any previous errors

      // Update profile info - store phone, email, address in preferences JSONB
      // First, get current preferences
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', workerId)
        .single();

      if (fetchError) {
        console.error('Error fetching current preferences:', fetchError);
        toast.error(t('admin.worker.fetchProfileError'));
        return;
      }

      const currentPreferences = isPlainObject(currentProfile?.preferences) ? currentProfile.preferences : {};
      const updatedPreferences = {
        ...currentPreferences,
        phone: profileInfo.phone || null,
        email: profileInfo.email || null,
        address: profileInfo.address || null,
      };

      // Update preferences JSONB
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          preferences: updatedPreferences,
        })
        .eq('id', workerId);

      if (profileError) {
        console.error('Error updating profile info:', profileError);
        toast.error(t('admin.worker.saveProfileError'));
        return;
      }

      // Zapis emaila kontaktowego do cleaning_staff (BEZPIECZNA STRATEGIA: Check -> Update OR Insert)
      const { data: existingStaff } = await supabase
        .from('cleaning_staff')
        .select('id')
        .eq('id', workerId)
        .maybeSingle();

      if (existingStaff) {
        const { error: updateError } = await supabase
          .from('cleaning_staff')
          .update({ contact_email: profileInfo.email ?? null })
          .eq('id', workerId);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('cleaning_staff')
          .insert({
            id: workerId,
            org_id: currentOrgId,
            full_name: worker?.full_name ?? 'Pracownik',
            contact_email: profileInfo.email ?? null,
            status: 'active',
          });
        if (insertError) throw insertError;
      }

      toast.success(t('admin.worker.profileSaved'));
      setIsEditingProfileInfo(false);
      await fetchWorkerData();
    } catch (error) {
      console.error('Error saving profile info:', error);
      toast.error(t('admin.worker.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!workerId || !worker) return;
    setIsLoading(true);

    try {
      // 1. Zapis PROFILES (Tylko Imię i Telefon – emaila tu nie ruszamy!)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: worker.full_name,
          phone: worker.phone_number,
          // USUNIĘTO: email (nie aktualizujemy auth emaila tutaj)
        })
        .eq('id', workerId);

      if (profileError) throw profileError;

      // 2. Zapis CLEANING_STAFF (Strategia: Check -> Update OR Insert)
      const emergencyContactForDb =
        worker.emergency_contact != null && typeof worker.emergency_contact === 'object' && !Array.isArray(worker.emergency_contact)
          ? JSON.stringify(worker.emergency_contact)
          : typeof worker.emergency_contact === 'string'
            ? worker.emergency_contact
            : null;

      const { data: existingStaff } = await supabase
        .from('cleaning_staff')
        .select('id')
        .eq('id', workerId)
        .maybeSingle();

      if (existingStaff) {
        // SCENARIUSZ 1: AKTUALIZACJA (UPDATE) – tylko zmienne pola, bez full_name
        console.log('Rekord istnieje - wykonuję UPDATE (bez full_name)');
        const { error: updateError } = await supabase
          .from('cleaning_staff')
          .update({
            contact_email: worker.email ?? null,
            notes: worker.notes ?? null,
            emergency_contact: emergencyContactForDb,
            emergency_phone: worker.emergency_phone ?? null,
            status: worker.status,
            employment_type: worker.employment_type,
          })
          .eq('id', workerId);

        if (updateError) throw updateError;
      } else {
        // SCENARIUSZ 2: TWORZENIE (INSERT) – pełny pakiet z full_name
        console.log('Brak rekordu - wykonuję INSERT (z full_name)');
        const { error: insertError } = await supabase
          .from('cleaning_staff')
          .insert({
            id: workerId,
            org_id: currentOrgId,
            full_name: worker.full_name || 'Pracownik',
            phone: worker.phone_number ?? worker.phone ?? null,
            contact_email: worker.email ?? null,
            notes: worker.notes ?? null,
            emergency_contact: emergencyContactForDb,
            emergency_phone: worker.emergency_phone ?? null,
            status: worker.status || 'active',
            employment_type: worker.employment_type,
          });

        if (insertError) throw insertError;
      }

      toast.success(t('admin.worker.updateSuccess'));
      setIsEditingProfileInfo(false);
      await fetchWorkerData();
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error(t('admin.worker.saveFailed', { message: error.message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEditProfileInfo = () => {
    if (worker) {
      setProfileInfo({
        phone: worker.phone ?? '',
        email: worker.email ?? workerEmail,
        address: worker.address ?? '',
      });
    }
    setEmailError(''); // Clear validation errors
    setIsEditingProfileInfo(false);
  };

  const handleEmailChange = (value: string) => {
    setProfileInfo({ ...profileInfo, email: value });
    setWorker((prev) => (prev ? { ...prev, email: value } : null));
    if (value.trim() === '') {
      setEmailError('');
    } else if (!validateEmail(value)) {
      setEmailError('invalid');
    } else {
      setEmailError('');
    }
  };

  const handleAddLocation = async () => {
    if (!selectedLocationId || !workerId || !currentOrgId) return;

    try {
      const { alreadyAssigned } = await grantStaffLocationAccess(selectedLocationId, workerId);
      if (alreadyAssigned) {
        toast.error(t('admin.worker.alreadyAssigned'));
        return;
      }

      toast.success(t('admin.worker.accessAdded'));
      setIsAddLocationDialogOpen(false);
      setSelectedLocationId('');
      await fetchLocations(workerId);
    } catch (error: any) {
      console.error('Error adding location:', error);
      toast.error(error?.message || t('admin.worker.unexpectedError'));
    }
  };

  const handleRemoveLocation = async (locationId: string) => {
    if (!workerId || !currentOrgId) return;

    try {
      setIsSaving(true);

      await revokeStaffLocationAccess(locationId, workerId);

      toast.success(t('admin.worker.accessRemoved'));
      await fetchLocations(workerId);
    } catch (error: any) {
      console.error('Error removing location:', error);
      toast.error(error?.message || t('admin.worker.unexpectedError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error(t('admin.worker.enterPassword'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('admin.worker.passwordsMismatch'));
      return;
    }

    const pinValidation = validatePIN(newPassword.trim());
    if (!pinValidation.allValid) {
      const errors: string[] = [];
      if (!pinValidation.isSixDigits) errors.push(t('admin.worker.pinSixDigits'));
      if (!pinValidation.isNotSimple) errors.push(t('admin.worker.pinNotSimple'));
      toast.error(t('admin.worker.pinRequirements', { errors: errors.join(', ') }));
      return;
    }

    if (!workerId) {
      toast.error(t('admin.worker.missingWorkerId'));
      return;
    }

    try {
      setIsChangingPassword(true);

      // For simplified accounts, we need to update password for the worker
      // Use admin API if available, otherwise use regular updateUser (which only works for current user)
      // Note: In browser context, admin API may not be available
      // We'll try to use admin.updateUserById if available
      let error = null;

      try {
        // Try admin API first (if available)
        const { error: adminError } = await supabase.auth.admin.updateUserById(workerId, {
          password: newPassword,
        });
        error = adminError;
      } catch (adminError) {
        // Admin API not available, try regular update (will only work if workerId === current user)
        console.warn('Admin API not available, trying regular update:', adminError);
        if (workerId) {
          // For now, we'll show an error that admin access is needed
          // In production, this should be handled by a backend API endpoint
          toast.error(t('admin.worker.passwordAdminRequired'));
          return;
        }
      }

      if (error) {
        console.error('Error changing password:', error);
        toast.error(t('admin.worker.passwordChangeError', { message: error.message }));
        return;
      }

      toast.success(t('admin.worker.passwordChanged'));
      setIsChangePasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(t('admin.worker.passwordChangeUnexpected'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAddAdjustment = async () => {
    if (!workerId || !currentOrgId) return;

    const amount = parseFloat(newAdjustment.amount);
    if (isNaN(amount) || amount === 0) {
      toast.error(t('admin.worker.invalidAmount'));
      return;
    }

    if (!newAdjustment.description.trim()) {
      toast.error(t('admin.worker.enterAdjustmentDescription'));
      return;
    }

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('staff_financial_adjustments')
        .insert({
          user_id: workerId,
          amount: amount,
          reason: newAdjustment.description.trim(),
          type: amount >= 0 ? 'bonus' : 'potrącenie',
        });

      if (error) {
        console.error('Error adding adjustment:', error);
        toast.error(t('admin.worker.addAdjustmentError'));
        return;
      }

      toast.success(t('admin.worker.adjustmentAdded'));
      setIsAddAdjustmentDialogOpen(false);
      setNewAdjustment({ amount: '', description: '' });
      await calculateMonthlyPayroll();
    } catch (error) {
      console.error('Error adding adjustment:', error);
      toast.error(t('admin.worker.genericError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!workerId || !currentOrgId || !payrollData || selectedTaskIds.size === 0) return;

    const selectedDetails = payrollData.details.filter((d) => d.task_id && selectedTaskIds.has(d.task_id));
    const includesFixedSalary = selectedDetails.some((d) => d.task_id?.startsWith('fixed-salary-'));
    const taskIdsFromSelection = selectedDetails
      .filter((d) => d.task_id && !d.task_id.startsWith('fixed-salary-'))
      .map((d) => d.task_id!);

    // Pre-flight check: verify which tasks are still unpaid (race condition protection)
    let finalTaskIds: string[] = [];
    if (taskIdsFromSelection.length > 0) {
      const { data: verifiedTasks, error: verifyError } = await supabase
        .from('cleaning_tasks')
        .select('id')
        .in('id', taskIdsFromSelection)
        .eq('assigned_staff_id', workerId)
        .eq('org_id', currentOrgId)
        .or('is_paid.is.null,is_paid.eq.false');

      if (verifyError) {
        console.error('Pre-flight verification error:', verifyError);
        toast.error(t('admin.worker.verifyTasksError'));
        return;
      }
      finalTaskIds = (verifiedTasks || []).map((t) => t.id);
    }

    // Rebuild selectedDetails: only verified unpaid tasks + fixed salary (virtual rows)
    const verifiedDetails = selectedDetails.filter(
      (d) =>
        d.task_id &&
        (d.task_id.startsWith('fixed-salary-') || finalTaskIds.includes(d.task_id))
    );

    const hasNothingToPay =
      verifiedDetails.length === 0 ||
      verifiedDetails.reduce((sum, d) => sum + d.amount, 0) <= 0;

    if (hasNothingToPay) {
      toast.error(
        t('admin.worker.alreadyPaidByOther')
      );
      setSelectedTaskIds(new Set());
      await calculateMonthlyPayroll();
      return;
    }

    // Group by month for payroll: (month, year) -> amount (based on verified details only)
    const amountByMonth = new Map<string, number>();
    for (const d of verifiedDetails) {
      let month: number;
      let year: number;
      if (d.task_id?.startsWith('fixed-salary-')) {
        const match = d.task_id.match(/fixed-salary-(\d+)-(\d+)/);
        if (match) {
          month = parseInt(match[1], 10);
          year = parseInt(match[2], 10);
        } else continue;
      } else if (d.date) {
        const dt = new Date(d.date);
        month = dt.getMonth() + 1;
        year = dt.getFullYear();
      } else continue;
      const key = `${year}-${month}`;
      amountByMonth.set(key, (amountByMonth.get(key) || 0) + d.amount);
    }

    const selectedAmount = verifiedDetails.reduce((sum, d) => sum + d.amount, 0);
    if (selectedAmount <= 0) return;

    try {
      setIsMarkingAsPaid(true);

      const { data: userData } = await supabase.auth.getUser();
      const paidBy = userData.user?.id || null;
      const notes = includesFixedSalary ? 'Wypłata zawiera stałe wynagrodzenie' : undefined;

      for (const [key, amount] of amountByMonth) {
        const [yearStr, monthStr] = key.split('-');
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);

        const { data: existingPayout } = await supabase
          .from('staff_payouts')
          .select('id, total_amount, notes')
          .eq('staff_id', workerId)
          .eq('org_id', currentOrgId)
          .eq('month', month)
          .eq('year', year)
          .maybeSingle();

        if (existingPayout) {
          const newTotal = parseFloat(String(existingPayout.total_amount)) + amount;
          const newNotes = [existingPayout.notes, notes].filter(Boolean).join('; ');
          const { error } = await supabase
            .from('staff_payouts')
            .update({ total_amount: newTotal, notes: newNotes || null })
            .eq('id', existingPayout.id);
          if (error) {
            console.error('Error updating payout:', error);
            toast.error(t('admin.worker.markPaidError'));
            return;
          }
        } else {
          const { error } = await supabase
            .from('staff_payouts')
            .insert({
              staff_id: workerId,
              org_id: currentOrgId,
              month,
              year,
              total_amount: amount,
              paid_by: paidBy,
              notes: notes ?? null,
            });
          if (error) {
            console.error('Error marking as paid:', error);
            toast.error(t('admin.worker.markPaidError'));
            return;
          }
        }
      }

      // Update only VERIFIED unpaid tasks (exclude virtual fixed-salary row) to is_paid = true
      if (finalTaskIds.length > 0) {
        const { error: updateError } = await supabase
          .from('cleaning_tasks')
          .update({ is_paid: true })
          .in('id', finalTaskIds);
        if (updateError) {
          console.error('Error setting is_paid on tasks:', updateError);
          toast.warning(t('admin.worker.markPaidPartial'));
        }
      }

      toast.success(t('admin.worker.settled'));
      setSelectedTaskIds(new Set());
      await calculateMonthlyPayroll();
      setWorkLogsPage(0);
      setWorkLogsHasMore(true);
      await fetchWorkLogs(workerId, 0, false);
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error(t('admin.worker.genericError'));
    } finally {
      setIsMarkingAsPaid(false);
    }
  };

  if (isLoading && !isLoadError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isLoadError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <p className="text-lg font-medium text-muted-foreground">
            {t('admin.worker.loadError')}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => { setIsLoadError(false); fetchWorkerData(); }} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.tryAgain')}
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              {t('admin.worker.refreshPage')}
            </Button>
          </div>
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('admin.worker.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">{t('admin.worker.notFound')}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('admin.worker.back')}
          </Button>
        </div>
      </div>
    );
  }

  const initials = worker.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <Avatar className="h-16 w-16">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{worker.full_name || t('admin.worker.noName')}</h1>
              <Badge variant={worker.account_type === 'hub' ? 'default' : 'secondary'}>
                {worker.account_type === 'hub' ? t('admin.worker.accountHub') : t('admin.worker.accountSimplified')}
              </Badge>
              {worker.is_archived && (
                <Badge variant="secondary" className="bg-amber-500 text-white">
                  {t('admin.worker.archived')}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Financial Rates Card */}
      {canViewOrgFinancials && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <CardTitle>{t('admin.worker.ratesTitle')}</CardTitle>
            </div>
            {canEdit && (
              <>
                {isEditingFinancials ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEditFinancials}
                      disabled={isSaving}
                      className="flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      {t('common.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveFinancials}
                      disabled={isSaving}
                      className="flex items-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('common.saveProgress')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          {t('common.save')}
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingFinancials(true)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {t('admin.worker.edit')}
                  </Button>
                )}
              </>
            )}
          </div>
          <CardDescription>
            {t('admin.worker.ratesDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="base-rate">{t('admin.worker.baseRate')}</Label>
              {isEditingFinancials ? (
                <Input
                  id="base-rate"
                  type="number"
                  step="0.01"
                  value={editedFinancialData.base_hourly_rate}
                  onChange={(e) => setEditedFinancialData({
                    ...editedFinancialData,
                    base_hourly_rate: parseFloat(e.target.value) || 0,
                  })}
                />
              ) : (
                <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                  {t('admin.worker.perHour', { value: worker.base_hourly_rate?.toFixed(2) || '0.00' })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="snow-rate">{t('admin.worker.snowRate')}</Label>
              {isEditingFinancials ? (
                <Input
                  id="snow-rate"
                  type="number"
                  step="0.01"
                  value={editedFinancialData.snow_hourly_rate}
                  onChange={(e) => setEditedFinancialData({
                    ...editedFinancialData,
                    snow_hourly_rate: parseFloat(e.target.value) || 0,
                  })}
                />
              ) : (
                <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                  {t('admin.worker.perHour', { value: worker.snow_hourly_rate?.toFixed(2) || '0.00' })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="extra-rate">{t('admin.worker.extraRate')}</Label>
              {isEditingFinancials ? (
                <Input
                  id="extra-rate"
                  type="number"
                  step="0.01"
                  value={editedFinancialData.extra_hourly_rate}
                  onChange={(e) => setEditedFinancialData({
                    ...editedFinancialData,
                    extra_hourly_rate: parseFloat(e.target.value) || 0,
                  })}
                />
              ) : (
                <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                  {t('admin.worker.perHour', { value: worker.extra_hourly_rate?.toFixed(2) || '0.00' })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="fixed-salary">{t('admin.worker.fixedSalary')}</Label>
              {isEditingFinancials ? (
                <Input
                  id="fixed-salary"
                  type="number"
                  step="0.01"
                  value={editedFinancialData.fixed_salary}
                  onChange={(e) => setEditedFinancialData({
                    ...editedFinancialData,
                    fixed_salary: parseFloat(e.target.value) || 0,
                  })}
                />
              ) : (
                <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                  {t('admin.worker.perMonth', { value: worker.fixed_salary?.toFixed(2) || '0.00' })}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="locations">{t('admin.worker.tabLocations')}</TabsTrigger>
          <TabsTrigger value="activity-log">{t('admin.worker.tabActivity')}</TabsTrigger>
          {canViewOrgFinancials && (
            <TabsTrigger value="payroll">{t('admin.worker.tabPayroll')}</TabsTrigger>
          )}
          <TabsTrigger value="data">{t('admin.worker.tabData')}</TabsTrigger>
        </TabsList>

        {/* Locations Tab */}
        <TabsContent value="locations" forceMount className="space-y-4 data-[state=inactive]:hidden">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.worker.locationsTitle')}</CardTitle>
                  <CardDescription>
                    {t('admin.worker.locationsDescription')}
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      fetchAvailableLocations();
                      setIsAddLocationDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('admin.worker.addToLocation')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {locations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {t('admin.worker.noLocations')}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.locationName')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.address')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.yourRole')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.lastVisit')}</TableHead>
                      {canEdit && (
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.actions')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.map(location => (
                      <TableRow key={location.id}>
                        <TableCell className="font-medium">
                          <Link
                            to={`/properties/${location.id}`}
                            className="text-primary hover:underline"
                          >
                            {location.address.split(',')[0] || location.address}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                            <Link
                              to={`/properties/${location.id}`}
                              className="text-primary hover:underline"
                            >
                              {location.address}
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell>{t('admin.worker.primaryCleaner')}</TableCell>
                        <TableCell>
                          {location.last_visit
                            ? format(new Date(location.last_visit), 'dd.MM.yyyy HH:mm', { locale: dateLocale })
                            : t('admin.worker.none')}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveLocation(location.id)}
                              disabled={isSaving}
                              className="text-destructive hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Tab */}
        {canViewOrgFinancials && (
        <TabsContent value="payroll" forceMount className="space-y-4 data-[state=inactive]:hidden">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.worker.payrollTitle')}</CardTitle>
                  <CardDescription>
                    {t('admin.worker.payrollDescription')}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && payrollData && (
                    <Button
                      variant="default"
                      onClick={handleMarkAsPaid}
                      disabled={
                        isMarkingAsPaid ||
                        selectedTaskIds.size === 0 ||
                        payrollData.details.filter((d) => d.task_id && selectedTaskIds.has(d.task_id)).reduce((s, d) => s + d.amount, 0) <= 0
                      }
                      className="flex items-center gap-2"
                    >
                      {isMarkingAsPaid ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('admin.worker.markingPaid')}
                        </>
                      ) : selectedTaskIds.size > 0 ? (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          {t('admin.worker.markSelectedPaid', { amount: payrollData.details.filter((d) => d.task_id && selectedTaskIds.has(d.task_id)).reduce((sum, d) => sum + d.amount, 0).toFixed(2) })}
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          {t('admin.worker.selectTasksToPay')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Range Picker and Month/Year Selector */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label>{t('admin.worker.dateRange')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[280px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {payrollDateRange.from ? (
                          payrollDateRange.to ? (
                            <>
                              {format(payrollDateRange.from, "dd MMM yyyy", { locale: dateLocale })} -{" "}
                              {format(payrollDateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                            </>
                          ) : (
                            format(payrollDateRange.from, "dd MMM yyyy", { locale: dateLocale })
                          )
                        ) : (
                          <span>{t('admin.worker.selectDateRange')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={payrollDateRange.from}
                        selected={{
                          from: payrollDateRange.from,
                          to: payrollDateRange.to,
                        }}
                        onSelect={(range) => {
                          setPayrollDateRange({
                            from: range?.from,
                            to: range?.to,
                          });
                        }}
                        numberOfMonths={2}
                        locale={dateLocale}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {(payrollDateRange.from || payrollDateRange.to) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPayrollDateRange({ from: undefined, to: undefined });
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('admin.worker.clearRange')}
                  </Button>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-sm text-muted-foreground">{t('admin.worker.orSelectMonth')}</span>
                  <Select
                    value={selectedMonth.toString()}
                    onValueChange={(value) => {
                      setSelectedMonth(parseInt(value));
                      setPayrollDateRange({ from: undefined, to: undefined });
                    }}
                  >
                    <SelectTrigger id="payroll-month" className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <SelectItem key={month} value={month.toString()}>
                          {format(new Date(2024, month - 1, 1), 'LLLL', { locale: dateLocale })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(value) => {
                      setSelectedYear(parseInt(value));
                      setPayrollDateRange({ from: undefined, to: undefined });
                    }}
                  >
                    <SelectTrigger id="payroll-year" className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isLoadingPayroll ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : payrollData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">{t('admin.worker.paid')}</CardTitle>
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{t('admin.worker.amountZl', { amount: payrollData.paidAmount.toFixed(2) })}</div>
                        <p className="text-xs text-muted-foreground">
                          {t('admin.worker.paidTasksSum')}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">{t('admin.worker.toPay')}</CardTitle>
                        {payrollData.totalAmount > 0 && payrollData.unpaidAmount === 0 ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {t('admin.worker.allPaid')}
                          </Badge>
                        ) : payrollData.paidAmount > 0 && payrollData.unpaidAmount > 0 ? (
                          <Badge variant="secondary" className="bg-amber-500/90 text-white hover:bg-amber-500/90">
                            {t('admin.worker.partiallyPaid')}
                          </Badge>
                        ) : payrollData.totalAmount > 0 ? (
                          <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400">
                            {t('admin.worker.toPay')}
                          </Badge>
                        ) : null}
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{t('admin.worker.amountZl', { amount: payrollData.unpaidAmount.toFixed(2) })}</div>
                        <p className="text-xs text-muted-foreground">
                          {t('admin.worker.remainingToPay', { amount: payrollData.unpaidAmount.toFixed(2) })}
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">{t('admin.worker.totalEarnings')}</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{t('admin.worker.amountZl', { amount: payrollData.totalEarnings.toFixed(2) })}</div>
                        <p className="text-xs text-muted-foreground">
                          {payrollDateRange.from && payrollDateRange.to
                            ? t('admin.worker.forPeriod', { from: format(payrollDateRange.from, 'dd.MM.yyyy', { locale: dateLocale }), to: format(payrollDateRange.to, 'dd.MM.yyyy', { locale: dateLocale }) })
                            : t('admin.worker.forMonth', { period: format(new Date(selectedYear, selectedMonth - 1, 1), 'LLLL yyyy', { locale: dateLocale }) })}
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">{t('admin.worker.hoursWorked')}</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{t('admin.worker.hoursValue', { hours: payrollData.totalHours.toFixed(2) })}</div>
                        <p className="text-xs text-muted-foreground">
                          {payrollDateRange.from && payrollDateRange.to
                            ? t('admin.worker.forPeriod', { from: format(payrollDateRange.from, 'dd.MM.yyyy', { locale: dateLocale }), to: format(payrollDateRange.to, 'dd.MM.yyyy', { locale: dateLocale }) })
                            : t('admin.worker.inSelectedPeriod')}
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">{t('admin.worker.baseRateCard')}</CardTitle>
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{t('admin.worker.perHour', { value: payrollData.baseRate.toFixed(2) })}</div>
                        <p className="text-xs text-muted-foreground">
                          {t('admin.worker.cleaningRate')}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Details Table */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>{t('admin.worker.payoutDetails')}</CardTitle>
                          <CardDescription>
                            {t('admin.worker.payoutDetailsDescription')}
                          </CardDescription>
                        </div>
                        {canEdit && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsAddAdjustmentDialogOpen(true)}
                            className="flex items-center gap-2"
                          >
                            <Plus className="h-4 w-4" />
                            {t('admin.worker.addAdjustment')}
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {payrollData.details.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                          {t('admin.worker.noDataForMonth')}
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="bg-slate-50 dark:bg-slate-800 w-12">
                                {canEdit && payrollData.details.some((d) => d.task_id && d.is_paid !== true) && (
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      id="payroll-select-all-unpaid"
                                      checked={
                                        payrollData.details
                                          .filter((d) => d.task_id && d.is_paid !== true)
                                          .every((d) => d.task_id && selectedTaskIds.has(d.task_id))
                                      }
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedTaskIds(
                                            new Set(
                                              payrollData.details
                                                .filter((d) => d.task_id && d.is_paid !== true)
                                                .map((d) => d.task_id!)
                                            )
                                          );
                                        } else {
                                          setSelectedTaskIds(new Set());
                                        }
                                      }}
                                    />
                                    <Label htmlFor="payroll-select-all-unpaid" className="text-xs font-normal cursor-pointer">
                                      {t('admin.worker.selectAllUnpaid')}
                                    </Label>
                                  </div>
                                )}
                              </TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.type')}</TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.description')}</TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.hours')}</TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.rate')}</TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.amount')}</TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.status')}</TableHead>
                              <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.date')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {payrollData.details.map((detail, index) => (
                              <TableRow key={detail.task_id ?? `adj-${index}`}>
                                <TableCell className="w-12">
                                  {detail.task_id ? (
                                    canEdit && detail.is_paid !== true ? (
                                      <Checkbox
                                        checked={selectedTaskIds.has(detail.task_id)}
                                        onCheckedChange={(checked) => {
                                          setSelectedTaskIds((prev) => {
                                            const next = new Set(prev);
                                            if (checked) next.add(detail.task_id!);
                                            else next.delete(detail.task_id!);
                                            return next;
                                          });
                                        }}
                                      />
                                    ) : (
                                      <Checkbox disabled className="opacity-50" />
                                    )
                                  ) : null}
                                </TableCell>
                                <TableCell>
                                  {detail.type === 'base' ? (
                                    <Badge variant="default" className="bg-blue-600">
                                      {t('admin.worker.base')}
                                    </Badge>
                                  ) : detail.type === 'bonus' ? (
                                    <Badge variant="default" className="bg-green-600">
                                      {t('admin.worker.bonus')}
                                    </Badge>
                                  ) : (
                                    <Badge variant="default" className="bg-orange-600">
                                      {t('admin.worker.overtimeAdjustment')}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{detail.description}</TableCell>
                                <TableCell>
                                  {detail.hours !== null ? t('admin.worker.hoursValue', { hours: detail.hours.toFixed(2) }) : '-'}
                                </TableCell>
                                <TableCell>
                                  {detail.rate !== null ? t('admin.worker.perHour', { value: detail.rate.toFixed(2) }) : '-'}
                                </TableCell>
                                <TableCell className="font-semibold">
                                  {t('admin.worker.amountZl', { amount: `${detail.amount >= 0 ? '+' : ''}${detail.amount.toFixed(2)}` })}
                                </TableCell>
                                <TableCell>
                                  {detail.task_id ? (
                                    detail.is_paid === true ? (
                                      <Badge className="bg-emerald-600 hover:bg-emerald-600">{t('admin.worker.paidBadge')}</Badge>
                                    ) : (
                                      <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400">
                                        {t('admin.worker.unpaidBadge')}
                                      </Badge>
                                    )
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {detail.date
                                    ? format(new Date(detail.date), 'dd.MM.yyyy', { locale: dateLocale })
                                    : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {t('admin.worker.noDataForPeriod')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Historia Zmian Wynagrodzenia */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-muted-foreground" />
                <CardTitle>{t('admin.worker.rateHistoryTitle')}</CardTitle>
              </div>
              <CardDescription>
                {t('admin.worker.rateHistoryDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingRateHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : rateHistory.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {t('admin.worker.noRateHistory')}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.changeDate')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.historyBaseRate')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.historySnowRate')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.historyExtraRate')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.historyFixedSalary')}</TableHead>
                      <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.changedBy')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rateHistory.map((entry, index) => {
                      const prev = rateHistory[index + 1];
                      const baseChanged = prev != null && entry.base_hourly_rate !== prev.base_hourly_rate;
                      const snowChanged = prev != null && entry.snow_hourly_rate !== prev.snow_hourly_rate;
                      const extraChanged = prev != null && entry.extra_hourly_rate !== prev.extra_hourly_rate;
                      const fixedChanged = prev != null && entry.fixed_salary !== prev.fixed_salary;
                      const highlightClass = 'font-semibold bg-emerald-50 dark:bg-emerald-950/40';
                      return (
                        <TableRow key={entry.id}>
                          <TableCell>
                            {format(new Date(entry.changed_at), 'dd.MM.yyyy', { locale: dateLocale })}
                          </TableCell>
                          <TableCell className={baseChanged ? highlightClass : ''}>
                            {t('admin.worker.amountPln', { amount: Number(entry.base_hourly_rate).toFixed(2) })}
                          </TableCell>
                          <TableCell className={snowChanged ? highlightClass : ''}>
                            {t('admin.worker.amountPln', { amount: Number(entry.snow_hourly_rate).toFixed(2) })}
                          </TableCell>
                          <TableCell className={extraChanged ? highlightClass : ''}>
                            {t('admin.worker.amountPln', { amount: Number(entry.extra_hourly_rate ?? 0).toFixed(2) })}
                          </TableCell>
                          <TableCell className={fixedChanged ? highlightClass : ''}>
                            {t('admin.worker.amountPln', { amount: Number(entry.fixed_salary).toFixed(2) })}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {entry.changed_by ? (entry.changed_by_display || t('admin.worker.changedByAdmin')) : t('admin.worker.changedBySystem')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Activity Log Tab – dwa moduły: Szczegółowa lista zadań + Rejestr czasu pracy */}
        <TabsContent value="activity-log" forceMount className="space-y-6 data-[state=inactive]:hidden">
          {/* A. Szczegółowa lista zadań (Activity Log) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.worker.activityTitle')}</CardTitle>
                  <CardDescription>
                    {activityLog.length > 0
                      ? t('admin.worker.activityShown', { count: activityLog.length, noun: activityLog.length === 1 ? t('admin.worker.activityTaskOne') : activityLog.length < 5 ? t('admin.worker.activityTaskFew') : t('admin.worker.activityTaskMany') })
                      : t('admin.worker.noCompletedWork')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Date Range Filter */}
              <div className="mb-4 flex items-center gap-4">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[280px] justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {activityDateRange.from ? (
                        activityDateRange.to ? (
                          <>
                            {format(activityDateRange.from, "dd MMM yyyy", { locale: dateLocale })} -{" "}
                            {format(activityDateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                          </>
                        ) : (
                          format(activityDateRange.from, "dd MMM yyyy", { locale: dateLocale })
                        )
                      ) : (
                        <span>{t('admin.worker.selectDateRange')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={activityDateRange.from}
                      selected={{
                        from: activityDateRange.from,
                        to: activityDateRange.to,
                      }}
                      onSelect={(range) => {
                        setActivityDateRange({
                          from: range?.from,
                          to: range?.to,
                        });
                      }}
                      numberOfMonths={2}
                      locale={dateLocale}
                    />
                  </PopoverContent>
                </Popover>
                {(activityDateRange.from || activityDateRange.to) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setActivityDateRange({ from: undefined, to: undefined });
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('admin.worker.clearFilter')}
                  </Button>
                )}
              </div>

              {isLoadingActivityLog ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : activityLog.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {t('admin.worker.noWorkInPeriod')}
                </p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.dateTime')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.placeSection')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.activityTask')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.taskDescription')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activityLog.map((task) => {
                        const location = task.cleaning_locations;
                        const locationAddress = location?.address ?? t('admin.worker.noAddress');
                        // Miejsce / Sekcja: wyłącznie nazwa pomieszczenia z joinu cleaning_tasks -> property_sections
                        const sectionName = task.sectionDisplayName ?? task.property_sections?.name ?? null;
                        const placeDisplay = sectionName || t('admin.worker.generalArea');
                        // Czynność: tytuł zadania (step_name / checklist) – bez wstawiania nazwy sekcji
                        let activityLabel = t('admin.worker.unknownTask');
                        const steps = task.task_step_logs ?? [];
                        if (steps.length > 0) {
                          const stepNames = steps.map((s) => s.step_name).filter(Boolean);
                          activityLabel = stepNames.length > 0 ? stepNames.join(', ') : activityLabel;
                        } else {
                          const checklistItems = parseChecklistItems(task.checklist);
                          if (checklistItems.length > 0) {
                            const firstItem = checklistItems[0];
                            activityLabel = firstItem.task_name || firstItem.name || activityLabel;
                          } else if (task.coordinator_notes?.trim()) {
                            activityLabel = task.coordinator_notes.trim().slice(0, 80);
                            if (task.coordinator_notes!.length > 80) activityLabel += '…';
                          }
                        }
                        const taskDescription = (task.coordinator_notes?.trim() || '').slice(0, 200);
                        const hasStaffNote = !!task.staff_notes?.trim();
                        return (
                          <TableRow
                            key={task.id}
                            className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                            onClick={() => {
                              setSelectedActivityTask(task);
                              setIsActivityDetailsDialogOpen(true);
                            }}
                          >
                            <TableCell>
                              {task.completed_at
                                ? format(new Date(task.completed_at), 'dd.MM.yyyy HH:mm', { locale: dateLocale })
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {/* Kolumna "Miejsce / Sekcja": property_sections.name lub "Obszar ogólny" */}
                              {task.location_id ? (
                                <Link
                                  to={`/properties/${task.location_id}`}
                                  className="text-primary hover:underline font-medium"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {placeDisplay}
                                </Link>
                              ) : (
                                <span>{placeDisplay}</span>
                              )}
                              {locationAddress && locationAddress !== placeDisplay && (
                                <div className="text-sm text-muted-foreground">{locationAddress}</div>
                              )}
                            </TableCell>
                            <TableCell title={task.coordinator_notes?.trim() || undefined}>
                              {activityLabel}
                            </TableCell>
                            <TableCell className="max-w-[220px]">
                              <div className="space-y-0.5">
                                {taskDescription && (
                                  <div className="text-xs text-muted-foreground truncate" title={task.coordinator_notes ?? undefined}>
                                    {taskDescription}{task.coordinator_notes && task.coordinator_notes.length > 200 ? '…' : ''}
                                  </div>
                                )}
                                {hasStaffNote && (
                                  <div className="text-xs text-muted-foreground italic" title={task.staff_notes ?? undefined}>
                                    {t('admin.worker.notePrefix')} {task.staff_notes!.trim().length > 80 ? `${task.staff_notes!.trim().slice(0, 80)}…` : task.staff_notes!.trim()}
                                  </div>
                                )}
                                {!taskDescription && !hasStaffNote && (
                                  <span className="text-muted-foreground text-xs">—</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="default" className="bg-green-600">
                                {t('admin.worker.completed')}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {activityHasMore && (
                    <div className="flex justify-center pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          const nextPage = activityPage + 1;
                          setActivityPage(nextPage);
                          fetchActivityLog(workerId!, nextPage, true);
                        }}
                        disabled={isLoadingMoreActivity}
                      >
                        {isLoadingMoreActivity ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {t('admin.worker.showMore')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* B. Rejestr czasu pracy (Work Logs) */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>{t('admin.worker.workLogTitle')}</CardTitle>
                <CardDescription>
                  {workLogs.length > 0
                    ? t('admin.worker.workLogShown', { count: workLogs.length, noun: workLogs.length === 1 ? t('admin.worker.workLogOne') : workLogs.length < 5 ? t('admin.worker.workLogFew') : t('admin.worker.workLogMany') })
                    : t('admin.worker.noWorkLogEntries')}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingWorkLogs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : workLogs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {t('admin.worker.noRegisteredTime')}
                </p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.date')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.property')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.hoursFromTo')}</TableHead>
                        <TableHead className="bg-slate-50 dark:bg-slate-800">{t('admin.worker.hoursSum')}</TableHead>
                        {canEdit && (
                          <TableHead className="bg-slate-50 dark:bg-slate-800 w-[100px]">{t('admin.worker.actions')}</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {workLogs.map((log) => {
                        const h = Math.floor(log.displayMinutes / 60);
                        const m = log.displayMinutes % 60;
                        const sumDisplay = h > 0 ? t('admin.worker.durationHm', { hours: h, minutes: m }) : t('admin.worker.durationMin', { minutes: m });
                        const actualH = Math.floor(log.totalMinutes / 60);
                        const actualM = log.totalMinutes % 60;
                        const actualDisplay = log.isSuspiciousLongSession
                          ? t('admin.worker.durationHm', { hours: actualH, minutes: actualM })
                          : null;
                        return (
                          <TableRow key={log.id} className={log.isSuspiciousLongSession ? 'bg-amber-50/50 dark:bg-amber-950/20' : undefined}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {format(new Date(log.date), 'dd.MM.yyyy', { locale: dateLocale })}
                                {log.is_paid && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="secondary" className="gap-1 text-xs">
                                          <Lock className="h-3 w-3" />
                                          {t('admin.worker.paidLocked')}
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{t('admin.worker.paidLockedTooltip')}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {log.is_open && (
                                  <Badge variant="outline" className="text-xs">{t('admin.worker.openSession')}</Badge>
                                )}
                                {(log.status === 'auto_closed' || log.close_reason === 'geofence' || log.close_reason === 'stale_timeout') && (
                                  <Badge variant="destructive" className="text-xs">
                                    {t('admin.worker.autoLogout')}
                                  </Badge>
                                )}
                                {log.needs_review && !log.is_open && (
                                  <Badge className="bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100 text-xs">
                                    {t('admin.worker.needsReview')}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {log.location_id ? (
                                <Link
                                  to={`/properties/${log.location_id}`}
                                  className="text-primary hover:underline"
                                >
                                  {(log.location_address || t('admin.worker.noAddress')).split(',')[0]}
                                </Link>
                              ) : (
                                (log.location_address || t('admin.worker.noAddress'))
                              )}
                            </TableCell>
                            <TableCell>
                              <div>
                                {format(new Date(log.started_at), 'dd.MM HH:mm', { locale: dateLocale })} –{' '}
                                {log.is_open ? t('admin.worker.ongoing') : format(new Date(log.completed_at), 'dd.MM HH:mm', { locale: dateLocale })}
                              </div>
                              {log.close_reason && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {workSessionCloseReasonLabel(log.close_reason as WorkSessionCloseReason)}
                                  {log.check_out_distance_meters != null ? ` · ${log.check_out_distance_meters} m` : ''}
                                </p>
                              )}
                            </TableCell>
                            <TableCell>
                              <span title={actualDisplay ?? undefined}>
                                {sumDisplay}
                                {log.isSuspiciousLongSession && (
                                  <span className="ml-1.5 text-amber-600 dark:text-amber-500 text-xs font-medium" title={actualDisplay ? t('admin.worker.suspiciousTitle', { time: actualDisplay }) : undefined}>
                                    {t('admin.worker.maxHoursReview', { hours: WORK_LOG_MAX_DISPLAY_HOURS })}
                                  </span>
                                )}
                              </span>
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {log.is_paid ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-flex">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8"
                                              disabled
                                            >
                                              <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 text-destructive"
                                              disabled
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('admin.worker.paidLockedTooltip')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => {
                                          setWorkLogEdit({ open: true, log });
                                          setEditWorkLogForm({
                                            started_at: new Date(log.started_at).toISOString().slice(0, 16),
                                            completed_at: new Date(log.completed_at).toISOString().slice(0, 16),
                                            edit_reason: '',
                                          });
                                        }}
                                        title={t('admin.worker.editEntry')}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                        onClick={() => setWorkLogDelete({ open: true, log })}
                                        title={t('admin.worker.deleteEntry')}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {workLogsHasMore && (
                    <div className="flex justify-center pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          const nextPage = workLogsPage + 1;
                          setWorkLogsPage(nextPage);
                          fetchWorkLogs(workerId!, nextPage, true);
                        }}
                        disabled={isLoadingMoreWorkLogs}
                      >
                        {isLoadingMoreWorkLogs ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {t('admin.worker.showMore')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Dialog edycji wpisu czasu pracy (Admin) */}
          <Dialog open={workLogEdit.open} onOpenChange={(open) => !open && setWorkLogEdit({ open: false, log: null })}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('admin.worker.editWorkLogTitle')}</DialogTitle>
                <DialogDescription>
                  {t('admin.worker.editWorkLogDescription')}
                </DialogDescription>
              </DialogHeader>
              {workLogEdit.log && (
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit-started-at">{t('admin.worker.start')}</Label>
                    <Input
                      id="edit-started-at"
                      type="datetime-local"
                      value={editWorkLogForm.started_at}
                      onChange={(e) => setEditWorkLogForm((prev) => ({ ...prev, started_at: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-completed-at">{t('admin.worker.end')}</Label>
                    <Input
                      id="edit-completed-at"
                      type="datetime-local"
                      value={editWorkLogForm.completed_at}
                      onChange={(e) => setEditWorkLogForm((prev) => ({ ...prev, completed_at: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-reason">{t('admin.worker.correctionReason')}</Label>
                    <Input
                      id="edit-reason"
                      value={editWorkLogForm.edit_reason}
                      onChange={(e) => setEditWorkLogForm((prev) => ({ ...prev, edit_reason: e.target.value }))}
                      placeholder={t('admin.worker.correctionReasonPlaceholder')}
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setWorkLogEdit({ open: false, log: null })}
                  disabled={isSavingWorkLog}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={isSavingWorkLog}
                  onClick={async () => {
                    const log = workLogEdit.log;
                    if (!log || !workerId) return;
                    const startedAt = editWorkLogForm.started_at;
                    const completedAt = editWorkLogForm.completed_at;
                    const reason = editWorkLogForm.edit_reason.trim();
                    if (!startedAt || !completedAt) {
                      toast.error(t('admin.worker.fillBothDates'));
                      return;
                    }
                    if (!reason) {
                      toast.error(t('admin.worker.enterCorrectionReason'));
                      return;
                    }
                    const startMs = new Date(startedAt).getTime();
                    const endMs = new Date(completedAt).getTime();
                    if (endMs < startMs) {
                      toast.error(t('admin.worker.endBeforeStart'));
                      return;
                    }
                    setIsSavingWorkLog(true);
                    try {
                      const { data: authData } = await supabase.auth.getUser();
                      if (!authData.user?.id) {
                        toast.error(t('admin.worker.noLoggedInUser'));
                        return;
                      }
                      await updateWorkSessionTimes({
                        sessionId: log.id,
                        startedAt: new Date(startedAt).toISOString(),
                        endedAt: new Date(completedAt).toISOString(),
                        editorId: authData.user.id,
                        reason,
                      });
                      toast.success(t('admin.worker.entryUpdated'));
                      setWorkLogEdit({ open: false, log: null });
                      setWorkLogsPage(0);
                      setWorkLogsHasMore(true);
                      await fetchWorkLogs(workerId, 0, false);
                    } catch (err) {
                      console.error('Error updating work log:', err);
                      toast.error(t('admin.worker.saveErrorPeriod'));
                    } finally {
                      setIsSavingWorkLog(false);
                    }
                  }}
                >
                  {isSavingWorkLog ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('common.save')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* AlertDialog usuwania wpisu czasu pracy (Admin) */}
          <AlertDialog open={workLogDelete.open} onOpenChange={(open) => !open && setWorkLogDelete({ open: false, log: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('admin.worker.deleteWorkLogTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('admin.worker.deleteWorkLogDescription')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeletingWorkLog}>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={isDeletingWorkLog}
                  onClick={async (e) => {
                    e.preventDefault();
                    const log = workLogDelete.log;
                    if (!log || !workerId) return;
                    setIsDeletingWorkLog(true);
                    try {
                      const { error } = await supabase.from('cleaning_work_sessions').delete().eq('id', log.id);
                      if (error) {
                        console.error('Error deleting work log:', error);
                        toast.error(t('admin.worker.deleteEntryFailed'));
                        return;
                      }
                      toast.success(t('admin.worker.entryDeleted'));
                      setWorkLogDelete({ open: false, log: null });
                      setWorkLogsPage(0);
                      setWorkLogsHasMore(true);
                      await fetchWorkLogs(workerId, 0, false);
                    } catch (err) {
                      console.error('Error deleting work log:', err);
                      toast.error(t('admin.worker.deleteUnexpected'));
                    } finally {
                      setIsDeletingWorkLog(false);
                    }
                  }}
                >
                  {isDeletingWorkLog ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('admin.worker.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* Data and Documents Tab */}
        <TabsContent value="data" forceMount className="space-y-4 data-[state=inactive]:hidden">
          {/* Three Column Grid: Profile Info, Emergency Contact, and Internal Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Profile Info Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('admin.worker.profileTitle')}</CardTitle>
                    <CardDescription>
                      {t('admin.worker.profileDescription')}
                    </CardDescription>
                  </div>
                  {canEdit && (
                    <>
                      {isEditingProfileInfo ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelEditProfileInfo}
                            disabled={isSaving}
                            className="flex items-center gap-2"
                          >
                            <X className="h-4 w-4" />
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveProfileInfo}
                            disabled={isSaving || !!emailError}
                            className="flex items-center gap-2"
                          >
                            {isSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('common.saveProgress')}
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4" />
                                {t('common.save')}
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditingProfileInfo(true)}
                          className="flex items-center gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          {t('admin.worker.edit')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="profile-phone">{t('admin.worker.phoneNumber')}</Label>
                  {isEditingProfileInfo ? (
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="profile-phone"
                        value={profileInfo.phone}
                        onChange={(e) => setProfileInfo({ ...profileInfo, phone: e.target.value })}
                        placeholder="+48 123 456 789"
                        className="pl-10"
                      />
                    </div>
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{profileInfo.phone || t('admin.worker.none')}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-email">{t('admin.worker.email')}</Label>
                  {isEditingProfileInfo ? (
                    <div className="space-y-1">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="profile-email"
                          type="email"
                          value={profileInfo.email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          placeholder="email@example.com"
                          className={`pl-10 ${emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        />
                      </div>
                      {emailError && (
                        <p className="text-xs text-red-500">{t('admin.worker.invalidEmail')}</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{profileInfo.email || workerEmail || t('admin.worker.none')}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-address">{t('admin.worker.homeAddress')}</Label>
                  {isEditingProfileInfo ? (
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Textarea
                        id="profile-address"
                        value={profileInfo.address}
                        onChange={(e) => setProfileInfo({ ...profileInfo, address: e.target.value })}
                        placeholder={t('admin.worker.addressPlaceholder')}
                        rows={3}
                        className="pl-10 resize-none"
                      />
                    </div>
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <span className="whitespace-pre-wrap">{profileInfo.address || t('admin.worker.none')}</span>
                    </div>
                  )}
                </div>
                {/* Simplified Account: Login and Reset Password */}
                {worker?.account_type === 'simplified' && (
                  <>
                    <div className="space-y-2 pt-2 border-t">
                      <Label htmlFor="profile-login">{t('admin.worker.login')}</Label>
                      <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{formatLoginDisplay(worker.login_email || workerEmail, t('admin.worker.none'))}</span>
                      </div>
                    </div>
                    {canEdit && !isEditingProfileInfo && (
                      <div className="mt-4 space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => setIsChangePasswordDialogOpen(true)}
                        >
                          <Lock className="mr-2 h-4 w-4" />
                          {t('admin.worker.changeWorkerPassword')}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          {t('admin.worker.changeWorkerPasswordHint')}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Emergency Contact Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('admin.worker.emergencyTitle')}</CardTitle>
                    <CardDescription>
                      {t('admin.worker.emergencyDescription')}
                    </CardDescription>
                  </div>
                  {canEdit && (
                    <>
                      {isEditingEmergencyContact ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelEditEmergencyContact}
                            disabled={isSaving}
                            className="flex items-center gap-2"
                          >
                            <X className="h-4 w-4" />
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveEmergencyContact}
                            disabled={isSaving}
                            className="flex items-center gap-2"
                          >
                            {isSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('common.saveProgress')}
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4" />
                                {t('common.save')}
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditingEmergencyContact(true)}
                          className="flex items-center gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          {t('admin.worker.edit')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="emergency-name">{t('admin.worker.fullName')}</Label>
                  {isEditingEmergencyContact ? (
                    <Input
                      id="emergency-name"
                      value={emergencyContact.name || ''}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, name: e.target.value })}
                      placeholder={t('admin.worker.namePlaceholder')}
                    />
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {emergencyContact.name || t('admin.worker.none')}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency-phone">{t('admin.worker.phone')}</Label>
                  {isEditingEmergencyContact ? (
                    <Input
                      id="emergency-phone"
                      value={emergencyContact.phone || ''}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, phone: e.target.value })}
                      placeholder="+48 123 456 789"
                    />
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {emergencyContact.phone || t('admin.worker.none')}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency-relationship">{t('admin.worker.relationship')}</Label>
                  {isEditingEmergencyContact ? (
                    <Input
                      id="emergency-relationship"
                      value={emergencyContact.relationship || ''}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, relationship: e.target.value })}
                      placeholder={t('admin.worker.relationshipPlaceholder')}
                    />
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {emergencyContact.relationship || t('admin.worker.none')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Internal Notes Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('admin.worker.notesTitle')}</CardTitle>
                    <CardDescription>
                      {t('admin.worker.notesDescription')}
                    </CardDescription>
                  </div>
                  {canEdit && (
                    <>
                      {isEditingInternalNotes ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelEditInternalNotes}
                            disabled={isSaving}
                            className="flex items-center gap-2"
                          >
                            <X className="h-4 w-4" />
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveInternalNotes}
                            disabled={isSaving}
                            className="flex items-center gap-2"
                          >
                            {isSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('common.saveProgress')}
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4" />
                                {t('common.save')}
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditingInternalNotes(true)}
                          className="flex items-center gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          {t('admin.worker.edit')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isEditingInternalNotes ? (
                  <Textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder={t('admin.worker.notesPlaceholder')}
                    rows={12}
                    className="resize-none"
                  />
                ) : (
                  <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 min-h-[200px] whitespace-pre-wrap">
                    {internalNotes || t('admin.worker.noNotes')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {currentOrgId && workerId && (
            <WorkerAssignedEquipmentCard
              orgId={currentOrgId}
              workerId={workerId}
              canEdit={canEdit}
            />
          )}
        </TabsContent>
      </Tabs>

      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.worker.manageTitle')}</CardTitle>
            <CardDescription>
              {t('admin.worker.manageDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {worker.is_archived ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-amber-500 text-white">
                      {t('admin.worker.archived')}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.worker.archivedHidden')}
                  </p>
                  <Button
                    onClick={handleRestoreWorker}
                    disabled={isSaving}
                    className="w-full"
                    variant="outline"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('admin.worker.restoring')}
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        {t('admin.worker.restoreWorker')}
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t('admin.worker.archiveHint')}
                  </p>
                  <Button
                    onClick={handleArchiveWorker}
                    disabled={isSaving}
                    variant="destructive"
                    className="w-full"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('admin.worker.archiving')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('admin.worker.archiveWorker')}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Location Dialog */}
      <Dialog open={isAddLocationDialogOpen} onOpenChange={setIsAddLocationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.worker.addAccessTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.worker.addAccessDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {availableLocations.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                {t('admin.worker.allLocationsAssigned')}
              </p>
            ) : (
              <SearchableSelect
                options={availableLocations.map(location => ({
                  value: location.id,
                  label: location.address,
                }))}
                value={selectedLocationId}
                onValueChange={setSelectedLocationId}
                placeholder={t('admin.worker.selectProperty')}
                searchPlaceholder={t('admin.worker.searchAddress')}
                emptyText={t('admin.worker.noAvailableProperties')}
              />
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddLocationDialogOpen(false);
                setSelectedLocationId('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddLocation}
              disabled={!selectedLocationId || availableLocations.length === 0}
            >
              {t('admin.worker.addAccess')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={isChangePasswordDialogOpen} onOpenChange={setIsChangePasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.worker.changePasswordTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.worker.changePasswordDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('admin.worker.newPassword')}</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('admin.worker.passwordMin')}
                maxLength={6}
              />
              <ValidationChecklist value={newPassword} type="pin" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('admin.worker.confirmPassword')}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('admin.worker.repeatPassword')}
                maxLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsChangePasswordDialogOpen(false);
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={
                isChangingPassword ||
                !newPassword ||
                !confirmPassword ||
                !validatePIN(newPassword).allValid
              }
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('admin.worker.changing')}
                </>
              ) : (
                t('admin.worker.changePassword')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Adjustment Dialog */}
      <Dialog open={isAddAdjustmentDialogOpen} onOpenChange={setIsAddAdjustmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.worker.adjustmentTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.worker.adjustmentDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="adjustment-amount">{t('admin.worker.amountZlLabel')}</Label>
              <Input
                id="adjustment-amount"
                type="number"
                step="0.01"
                value={newAdjustment.amount}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, amount: e.target.value })}
                placeholder={t('admin.worker.amountPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('admin.worker.amountHint')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjustment-description">{t('admin.worker.description')}</Label>
              <Textarea
                id="adjustment-description"
                value={newAdjustment.description}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, description: e.target.value })}
                placeholder={t('admin.worker.adjustmentDescriptionPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddAdjustmentDialogOpen(false);
                setNewAdjustment({ amount: '', description: '' });
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddAdjustment}
              disabled={isSaving || !newAdjustment.amount || !newAdjustment.description.trim()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('admin.worker.adding')}
                </>
              ) : (
                t('admin.worker.addAdjustment')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Details Dialog */}
      <Dialog open={isActivityDetailsDialogOpen} onOpenChange={setIsActivityDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.worker.activityDetailsTitle')}</DialogTitle>
            <DialogDescription>
              {selectedActivityTask?.cleaning_locations?.address && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedActivityTask.cleaning_locations.address}</span>
                  </div>
                  {selectedActivityTask.completed_at && (
                    <div className="flex items-center gap-2 mt-1">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {format(new Date(selectedActivityTask.completed_at), 'dd.MM.yyyy HH:mm', { locale: dateLocale })}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Staff Notes */}
            {selectedActivityTask?.staff_notes && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">{t('admin.worker.staffNotes')}</h3>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
                  <p className="whitespace-pre-wrap">{selectedActivityTask.staff_notes}</p>
                </div>
              </div>
            )}

            {/* Step Logs */}
            {selectedActivityTask?.task_step_logs && selectedActivityTask.task_step_logs.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold">{t('admin.worker.completedSteps')}</h3>
                <div className="space-y-3">
                  {selectedActivityTask.task_step_logs.map((log, index) => (
                    <div
                      key={log.id}
                      className="border border-border rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{index + 1}.</span>
                          <span className="font-medium">{log.step_name}</span>
                        </div>
                        {log.created_at && (
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(log.created_at), 'HH:mm', { locale: dateLocale })}
                          </span>
                        )}
                      </div>
                      {log.notes && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {log.notes}
                        </div>
                      )}
                      {log.photo_url && (
                        <div className="mt-2">
                          <StoragePhoto
                            pathOrUrl={log.photo_url}
                            fallbackBucket="photos"
                            alt={log.step_name}
                            className="rounded-lg max-w-full h-auto max-h-64 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Gallery */}
            {selectedActivityTask?.task_step_logs && 
             selectedActivityTask.task_step_logs.some(log => log.photo_url) && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">{t('admin.worker.photoGallery')}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {selectedActivityTask.task_step_logs
                    .filter(log => log.photo_url)
                    .map((log) => (
                      <div key={log.id} className="relative">
                        <StoragePhoto
                          pathOrUrl={log.photo_url!}
                          fallbackBucket="photos"
                          alt={log.step_name}
                          className="rounded-lg w-full h-48 object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(url) => {
                            window.open(url, "_blank");
                          }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2 rounded-b-lg">
                          {log.step_name}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {!selectedActivityTask?.staff_notes && 
             (!selectedActivityTask?.task_step_logs || selectedActivityTask.task_step_logs.length === 0) && (
              <p className="text-muted-foreground text-center py-4">
                {t('admin.worker.noExtraInfo')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsActivityDetailsDialogOpen(false)}
            >
              {t('admin.worker.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
